/* =====================================================
   Personalizer — assets/personalizer.js
   ===================================================== */
(function () {
  'use strict';

  /* --------------------------------------------------
     PZElement — one draggable/scalable/rotatable item
  -------------------------------------------------- */
  class PZElement {
    constructor(type, content, opts) {
      this.id         = 'pz_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
      this.type       = type;    // 'photo' | 'text' | 'svg'
      this.content    = content;
      this.x          = 0;
      this.y          = 0;
      this.xFrac      = 0.5; // position as fraction of safe zone (0–1), screen-independent
      this.yFrac      = 0.5;
      this.scale      = opts.scale ?? 1; // design-space scale (photo/svg: design px; text: CSS multiplier)
      this.rotation   = 0;
      this.preview    = opts.preview  || null; // dithered preview URL (alleen voor photo)
      this.naturalW   = opts.naturalW || 0;    // originele pixelbreedte foto
      this.naturalH   = opts.naturalH || 0;
      this.fontFamily   = opts.fontFamily   || "'Playfair Display', serif";
      // fontSizeFrac is the font size as a fraction of the safe zone height (0–1).
      // This is fully screen-independent: multiply by any canvas height to get pixel size.
      this.fontSizeFrac = opts.fontSizeFrac || 0.05;
      this.svgW         = opts.svgW || 100;
      this.svgH         = opts.svgH || 100;
      this.el           = null;
    }

    mount(container) {
      this.el = document.createElement('div');
      this.el.className = 'pz-element pz-' + this.type;
      this.el.dataset.pzId = this.id;
      this.el.setAttribute('tabindex', '0');
      this.el.setAttribute('role', 'img');
      this.el.setAttribute('aria-label', this._ariaLabel());
      this._buildDOM();
      container.appendChild(this.el);
      // Center in container
      this.x = container.offsetWidth  / 2;
      this.y = container.offsetHeight / 2;
      // Store screen-independent fractions immediately
      this.xFrac = container.offsetWidth  ? this.x / container.offsetWidth  : 0.5;
      this.yFrac = container.offsetHeight ? this.y / container.offsetHeight : 0.5;
      this._applyTransform();
      this._bindHandlers();
    }

    _ariaLabel() {
      if (this.type === 'photo') return 'Geüploade foto';
      if (this.type === 'text')  return 'Tekst: ' + this.content;
      return 'Symbool';
    }

    _buildDOM() {
      let inner = '';
      if (this.type === 'photo') {
        inner = '<img src="' + (this.preview || this.content) + '" alt="Geüploade foto" draggable="false">';
      } else if (this.type === 'text') {
        inner = '<span class="pz-text" style="font-family:' + this.fontFamily + ';font-size:16px">'
              + _esc(this.content) + '</span>'; // exact size set by _applyTransform()
      } else {
        inner = this.content.replace(/<svg/, '<svg width="' + this.svgW + '" height="' + this.svgH + '"');
      }
      // No inline handles — handles live in the global pz-handles-overlay
      this.el.innerHTML = '<div class="pz-element-content">' + inner + '</div>';
    }

    _applyTransform() {
      if (!this.el) return;
      this.el.style.left = this.x + 'px';
      this.el.style.top  = this.y + 'px';

      const p = window._personalizer;

      // For text: font size is set in display pixels (fontSizeFrac × safeZone height).
      // CSS scale(el.scale) is applied on top as a pure multiplier — no conversion needed.
      if (this.type === 'text' && p && p.safeZone && p.safeZone.offsetHeight) {
        const displayFs = Math.max(6, Math.round(this.fontSizeFrac * p.safeZone.offsetHeight));
        const span      = this.el.querySelector('.pz-text');
        if (span) {
          span.style.fontSize = displayFs + 'px';
        }
        // Width: clear JS override → CSS max-content takes effect → browser reports natural width.
        // Canvas measureText uses different font rendering than CSS (kerning, ligatures differ),
        // so reading offsetWidth after clearing gives the accurate browser-rendered width.
        this.el.style.width = '';
        const renderedW = this.el.offsetWidth;
        // Canvas for height only (ascent/descent metrics are reliable from canvas).
        const mc = this._mc || (this._mc = document.createElement('canvas').getContext('2d'));
        mc.font  = displayFs + 'px ' + this.fontFamily;
        const m  = mc.measureText(this.content || 'Mg');
        this.el.style.width = (renderedW || Math.ceil(mc.measureText(this.content || ' ').width)) + 'px';
        // actualBoundingBox* = precieze inktgrenzen voor DEZE tekst (niet font-breed maximum).
        // Dit stemt exact overeen met de SVG-paden die Python genereert voor dezelfde tekst.
        const fba    = m.actualBoundingBoxAscent  || m.fontBoundingBoxAscent  || displayFs * 0.8;
        const fbaMax = m.fontBoundingBoxAscent     || fba; // max ascent for any glyph in the font
        const fbd    = m.actualBoundingBoxDescent  || m.fontBoundingBoxDescent || displayFs * 0.2;
        const totalH = Math.ceil(fba + fbd);
        this.el.style.height = totalH + 'px';
        // CSS places the baseline at sTypoAscender from span-top, but canvas fontBoundingBoxAscent
        // may be winAscent (larger). Measure the actual CSS baseline with a zero-height anchor,
        // then shift the span so ink-top aligns exactly with div-top.
        // Tijdelijk rotatie verwijderen: getBoundingClientRect geeft anders scheefgetrokken
        // coördinaten waardoor marginTop fout wordt en de tekst buiten het frame valt.
        this.el.style.transform = 'translate(-50%,-50%) scale(' + this.scale + ')';
        if (span) {
          span.style.marginTop = '';
          const _anch = document.createElement('span');
          _anch.style.cssText = 'display:inline-block;width:0;height:0;vertical-align:baseline;overflow:hidden';
          span.appendChild(_anch);
          const _sr = span.getBoundingClientRect();
          const _ar = _anch.getBoundingClientRect();
          span.removeChild(_anch);
          const cssAsc = (_sr.height > 0 && this.scale > 0) ? (_ar.top - _sr.top) / this.scale : fbaMax;
          // Offset (totalH-fs)/2 verplaatst naar marginTop zodat de rotatiepivot op el.y
          // blijft — zelfde als canvas en Python, zodat rotatie 0°/180° identieke positie geeft.
          span.style.marginTop = Math.round(fba - cssAsc + (totalH - displayFs) / 2) + 'px';
        }
        this._textCenterOffset = (totalH - displayFs) / 2;
        this._inkHalfH = displayFs / 2; // ink top = el.y - _inkHalfH; used for drag clamping
        // Clamp pivot so ink stays inside the safe zone (fixes elements from old sessions)
        const szH = p.safeZone.offsetHeight;
        const clampedY = Math.max(this._inkHalfH, Math.min(szH - this._inkHalfH, this.y));
        if (clampedY !== this.y) {
          this.y = clampedY;
          this.yFrac = szH > 0 ? clampedY / szH : this.yFrac;
        }
        this.el.style.top = this.y + 'px';
      }

      // For photo/svg: el.scale is in design-space units.
      // Convert to CSS display scale: design_scale × (safeZone px / design canvas px).
      let cssScale = this.scale;
      if ((this.type === 'photo' || this.type === 'svg') && p && p.safeZone && p.safeZone.offsetWidth) {
        const { w: DW } = p._designSize();
        cssScale = this.scale * p.safeZone.offsetWidth / DW;
      }
      this.el.style.transform =
        'translate(-50%,-50%) rotate(' + this.rotation + 'deg) scale(' + cssScale + ')';

      // Reposition global handles overlay whenever this element moves/scales/rotates
      if (p && p.selected === this) p._updateHandlesOverlay();
    }

    _bindHandlers() {
      const p = window._personalizer;
      // Drag on content area
      this.el.querySelector('.pz-element-content')
        .addEventListener('pointerdown', (e) => {
          e.stopPropagation();
          p.select(this);
          p.startInteraction('drag', this, e);
        });
      // Keyboard delete
      this.el.addEventListener('keydown', (e) => {
        if ((e.key === 'Delete' || e.key === 'Backspace') && p.selected === this) {
          p.removeElement(this.id);
        }
      });
    }

    select()   { if (this.el) this.el.classList.add('is-selected'); }
    deselect() { if (this.el) this.el.classList.remove('is-selected'); }

    remove() {
      if (this.el && this.el.parentNode) this.el.parentNode.removeChild(this.el);
      this.el = null;
    }

    applyScale(s)    { this.scale    = s; this._applyTransform(); }
    applyRotation(r) { this.rotation = r; this._applyTransform(); }

    updateContent(newContent) {
      this.content = String(newContent);
      if (!this.el) return;
      const span = this.el.querySelector('.pz-text');
      if (span) span.textContent = this.content;
      this.el.setAttribute('aria-label', this._ariaLabel());
      this._applyTransform();
    }

    serialize() {
      const out = {
        type:         this.type,
        content:      this.content,
        x:            Math.round(this.x),
        y:            Math.round(this.y),
        scale:        +this.scale.toFixed(3),
        rotation:     Math.round(this.rotation),
        fontFamily:   this.fontFamily,
        fontSizeFrac: +this.fontSizeFrac.toFixed(6)
      };
      if (this.type === 'photo' && this.preview) out.preview = this.preview;
      if (this.type === 'svg' && (this.svgW !== 100 || this.svgH !== 100)) {
        out.svgW = this.svgW;
        out.svgH = this.svgH;
      }
      return out;
    }
  }

  /* --------------------------------------------------
     Personalizer — main controller
  -------------------------------------------------- */
  class Personalizer {
    constructor() {
      this.elements       = [];
      this.selected       = null;
      this.ix             = null; // active interaction
      this.safeContent    = null; // clips element content
      this.handlesOverlay = null; // holds rotate/scale/delete handles

      const cfg          = window.personalizerConfig || {};
      this.safeZoneCfg   = cfg.safeZone || { x: 18, y: 22, width: 64, height: 52 };
      this.variantId     = cfg.variantId || null;
      this.cartUrl       = cfg.cartUrl   || '/cart';
      this.qty           = 1;

      this._mapInstance  = null; // mapboxgl.Map
      this._mapContainer = null; // map div in safeContent
      this._mapZoomFloat = null; // floating zoom buttons on preview
      this._mapZoomTimer = null; // continuous zoom interval handle
      this._mapTitlePZ   = null; // PZElement for city name (draggable)
      this._mapCoordsPZ  = null; // PZElement for coordinates (draggable)
      this._mapMarkers   = new Set(); // PZElements for map markers (draggable, clamped to map)
      this._mapTabLoaded = false;
      this.mapState      = null; // { center, zoom, title, shape }
    }

    init() {
      this._findDOM();
      if (!this.modal) return;
      this._bindUI();
      this._bindGlobal();
    }

    _findDOM() {
      this.modal        = document.getElementById('personalizer-modal');
      this.triggerBtn   = document.querySelector('.personalizer-trigger-btn');
      this.safeZone     = document.getElementById('pz-safe-zone');
      this.productImage = document.getElementById('pz-product-image');
      this.imgWrapper   = document.getElementById('pz-image-wrapper');

      this.tabBtns       = document.querySelectorAll('.pz-tab');
      this.tabPanels     = document.querySelectorAll('.pz-tab-panel');

      this.photoInput    = document.getElementById('pz-photo-input');
      this.uploadArea    = document.getElementById('pz-upload-area');
      this.uploadTrigger = document.getElementById('pz-upload-trigger');

      this.textInput     = document.getElementById('pz-text-input');
      this.fontSelect    = document.getElementById('pz-font-select');
      this.fontSizeRange = document.getElementById('pz-font-size');
      this.fontSizeVal   = document.getElementById('pz-font-size-val');
      this.fontPreview   = document.getElementById('pz-font-preview');
      this.addTextBtn    = document.getElementById('pz-add-text-btn');

      this.svgItems      = document.querySelectorAll('.pz-svg-item');

      this.elControls    = document.getElementById('pz-element-controls');
      this.ctrlScale     = document.getElementById('pz-ctrl-scale');
      this.ctrlRotation  = document.getElementById('pz-ctrl-rotation');
      this.ctrlRotVal    = document.getElementById('pz-ctrl-rotation-val');
      this.deleteBtn     = document.getElementById('pz-delete-btn');

      this.confirmCheck  = document.getElementById('pz-design-confirm');
      this.atcBtn        = document.getElementById('pz-atc-btn');
      this.confirmBtn    = document.getElementById('pz-confirm-btn');
      this.qtyMinus      = document.getElementById('pz-qty-minus');
      this.qtyPlus       = document.getElementById('pz-qty-plus');
      this.qtyVal        = document.getElementById('pz-qty-val');

      this.tutorial    = document.getElementById('pz-tutorial');
      this.tutorialBtn = document.getElementById('pz-tutorial-btn');

      this.panelBody    = document.getElementById('pz-panel-body');
      this.optionsPanel = document.querySelector('.pz-options-panel');
      this.panelToggle  = document.getElementById('pz-panel-toggle');
      this.panelLabel   = document.getElementById('pz-panel-toggle-label');

      this.mobileBar  = document.getElementById('pz-mobile-bar');
      this.mbScale    = document.getElementById('pz-mb-scale');
      this.mbRotation = document.getElementById('pz-mb-rotation');
      this.mbDelete   = document.getElementById('pz-mb-delete');
      this.mbConfirm  = document.getElementById('pz-mb-confirm');

      this.atcDrawer    = document.getElementById('pz-atc-drawer');
      this.atcBtnLabel  = document.getElementById('pz-atc-btn-label');
      this.drawerClose  = document.getElementById('pz-drawer-close');
      this.cartTrigger  = document.getElementById('pz-cart-trigger');
      this.previewPanel = document.querySelector('.pz-preview-panel');

      this.mapGeocoderWrap      = document.getElementById('pz-map-geocoder-container');
      this.mapTitleInput        = document.getElementById('pz-map-title-input');
      this.mapCoordsEl          = document.getElementById('pz-map-coords');
      this.mapRemoveBtn         = document.getElementById('pz-map-remove-btn');
      this.mapNoConfig          = document.getElementById('pz-map-no-config');
      this.mapZoomInBtn         = document.getElementById('pz-map-zoom-in');
      this.mapZoomOutBtn        = document.getElementById('pz-map-zoom-out');
      this.mapPreviewBtn        = document.getElementById('pz-map-preview-btn');
      this.mapCustomCoordsCheck = document.getElementById('pz-map-custom-coords-check');
      this.mapCustomCoordsInput = document.getElementById('pz-map-custom-coords-input');
      this.mapMarkerRow         = document.getElementById('pz-map-marker-row');

      this.bgRotateBar = document.getElementById('pz-bg-rotate-bar');
      this.bgRotateCcw = document.getElementById('pz-bg-rotate-ccw');
      this.bgRotateCw  = document.getElementById('pz-bg-rotate-cw');
    }

    _bindUI() {
      // Open / close
      if (this.triggerBtn) {
        this.triggerBtn.addEventListener('click', () => this.open());
      }
      document.querySelectorAll('[data-personalizer-close]').forEach(el => {
        el.addEventListener('click', () => this.close());
      });

      // Tabs
      this.tabBtns.forEach(btn => {
        btn.addEventListener('click', () => this._switchTab(btn.dataset.tab));
      });

      // Background rotation (customer-facing)
      if (this.bgRotateCcw) {
        this.bgRotateCcw.addEventListener('click', () => {
          if (!this._allowedRotations || this._allowedRotations.length <= 1) return;
          var idx = (this._bgRotIdx - 1 + this._allowedRotations.length) % this._allowedRotations.length;
          this._confirmBgRotate(idx);
        });
      }
      if (this.bgRotateCw) {
        this.bgRotateCw.addEventListener('click', () => {
          if (!this._allowedRotations || this._allowedRotations.length <= 1) return;
          var idx = (this._bgRotIdx + 1) % this._allowedRotations.length;
          this._confirmBgRotate(idx);
        });
      }

      // Photo upload
      if (this.uploadTrigger) {
        this.uploadTrigger.addEventListener('click', (e) => {
          e.stopPropagation();
          this.photoInput && this.photoInput.click();
        });
      }
      if (this.uploadArea) {
        this.uploadArea.addEventListener('click', () => this.photoInput && this.photoInput.click());
        this.uploadArea.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') this.photoInput && this.photoInput.click();
        });
        this.uploadArea.addEventListener('dragover', (e) => {
          e.preventDefault();
          this.uploadArea.classList.add('is-dragover');
        });
        this.uploadArea.addEventListener('dragleave', () => {
          this.uploadArea.classList.remove('is-dragover');
        });
        this.uploadArea.addEventListener('drop', (e) => {
          e.preventDefault();
          this.uploadArea.classList.remove('is-dragover');
          const file = e.dataTransfer.files[0];
          if (file && file.type.startsWith('image/')) this._processPhoto(file);
        });
      }
      if (this.photoInput) {
        this.photoInput.addEventListener('change', () => {
          if (this.photoInput.files[0]) {
            this._processPhoto(this.photoInput.files[0]);
            this.photoInput.value = '';
          }
        });
      }

      // Text
      const updateFontPreview = () => {
        if (!this.fontPreview) return;
        const text = (this.textInput && this.textInput.value.trim()) || 'Voorbeeld tekst';
        const font = this.fontSelect    ? this.fontSelect.value    : '';
        const size = this.fontSizeRange ? this.fontSizeRange.value : 28;
        this.fontPreview.style.fontFamily = font;
        this.fontPreview.style.fontSize   = Math.min(28, +size) + 'px';
        this.fontPreview.textContent      = text;
        if (this.fontSizeVal) this.fontSizeVal.textContent = size;
      };
      if (this.textInput)     this.textInput.addEventListener('input', updateFontPreview);
      if (this.fontSelect)    this.fontSelect.addEventListener('change', updateFontPreview);
      if (this.fontSizeRange) this.fontSizeRange.addEventListener('input', updateFontPreview);
      updateFontPreview();

      if (this.addTextBtn) {
        this.addTextBtn.addEventListener('click', () => this._addText());
      }
      if (this.textInput) {
        this.textInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); this._addText(); }
        });
      }

      // SVGs
      this.svgItems.forEach(item => {
        item.addEventListener('click', () => this._addSVG(item));
      });

      // Element controls
      if (this.ctrlScale) {
        this.ctrlScale.addEventListener('input', () => {
          if (this.selected) {
            this.selected.applyScale(+this.ctrlScale.value);
          }
        });
      }
      if (this.ctrlRotation) {
        this.ctrlRotation.addEventListener('input', () => {
          if (this.selected) {
            this.selected.applyRotation(+this.ctrlRotation.value);
            if (this.ctrlRotVal) this.ctrlRotVal.textContent = this.ctrlRotation.value + '°';
          }
        });
      }
      if (this.deleteBtn) {
        this.deleteBtn.addEventListener('click', () => {
          if (this.selected) this.removeElement(this.selected.id);
        });
      }

      // Safe zone background click → deselect
      if (this.safeZone) {
        this.safeZone.addEventListener('pointerdown', (e) => {
          if (e.target === this.safeZone || e.target === this.safeContent) {
            this.deselectAll();
          }
        });
      }

      // Klikken buiten de safezone → deselecteer
      if (this.imgWrapper) {
        this.imgWrapper.addEventListener('pointerdown', (e) => {
          if (!this.safeZone || !this.safeZone.contains(e.target)) {
            this.deselectAll();
          }
        });
      }

      // Klikken buiten de productafbeelding (modal backdrop) → deselecteer
      // Sluit het volledige options-panel uit (mobile bar, toggle area en panel body)
      if (this.modal) {
        this.modal.addEventListener('pointerdown', (e) => {
          if (this.imgWrapper && this.imgWrapper.contains(e.target)) return;
          if (this.optionsPanel && this.optionsPanel.contains(e.target)) return;
          this.deselectAll();
        });
      }

      // ATC stap 1: open bevestiging-drawer
      if (this.atcBtn) {
        this.atcBtn.addEventListener('click', () => this._toggleAtcDrawer());
      }
      // ATC stap 2: bevestigen + toevoegen
      if (this.confirmCheck) {
        this.confirmCheck.addEventListener('change', () => this._updateAtcState());
      }
      if (this.confirmBtn) {
        this.confirmBtn.addEventListener('click', () => this._saveDesignAndAddToCart());
      }
      // Hoeveelheid
      if (this.qtyMinus) {
        this.qtyMinus.addEventListener('click', () => {
          this.qty = Math.max(1, (this.qty || 1) - 1);
          if (this.qtyVal) this.qtyVal.textContent = this.qty;
        });
      }
      if (this.qtyPlus) {
        this.qtyPlus.addEventListener('click', () => {
          this.qty = (this.qty || 1) + 1;
          if (this.qtyVal) this.qtyVal.textContent = this.qty;
        });
      }

      // Tutorial dismiss
      if (this.tutorialBtn) {
        this.tutorialBtn.addEventListener('click', () => this._hideTutorial());
      }
      // Mobile panel toggle
      if (this.panelToggle) {
        this.panelToggle.addEventListener('click', () => this._togglePanel());
      }

      // Mobile cart trigger → opens ATC drawer
      if (this.cartTrigger) {
        this.cartTrigger.addEventListener('click', () => this._toggleAtcDrawer());
      }
      // Mobile drawer close button
      if (this.drawerClose) {
        this.drawerClose.addEventListener('click', () => this._toggleAtcDrawer());
      }

      // Mobile compact controls
      if (this.mbScale) {
        this.mbScale.addEventListener('input', () => {
          if (this.selected) {
            this.selected.applyScale(+this.mbScale.value);
            if (this.ctrlScale) this.ctrlScale.value = this.mbScale.value;
          }
        });
      }
      if (this.mbRotation) {
        this.mbRotation.addEventListener('input', () => {
          if (this.selected) {
            this.selected.applyRotation(+this.mbRotation.value);
            if (this.ctrlRotation) this.ctrlRotation.value = this.mbRotation.value;
            if (this.ctrlRotVal)   this.ctrlRotVal.textContent = Math.round(+this.mbRotation.value) + '°';
          }
        });
      }
      if (this.mbDelete) {
        this.mbDelete.addEventListener('click', () => {
          if (this.selected) this.removeElement(this.selected.id);
        });
      }
      if (this.mbConfirm) {
        this.mbConfirm.addEventListener('click', () => this._expandPanel());
      }

      // Mobile: tap on preview area (outside options panel) collapses the panel
      if (this.previewPanel) {
        this.previewPanel.addEventListener('pointerdown', () => {
          if (this._isMobile() && this.panelBody && !this.panelBody.classList.contains('is-collapsed')) {
            this._collapsePanel();
          }
        });
      }

      // Stadskaart
      if (this.mapRemoveBtn) {
        this.mapRemoveBtn.addEventListener('click', () => this._removeMap());
      }
      if (this.mapMarkerRow) {
        this.mapMarkerRow.querySelectorAll('.pz-map-marker-icon-btn').forEach(btn => {
          btn.addEventListener('click', () => this._addMapMarker(btn.dataset.marker));
        });
      }
      if (this.mapTitleInput) {
        this.mapTitleInput.addEventListener('input', () => {
          const val = this.mapTitleInput.value;
          if (this.mapState) this.mapState.title = val;
          if (this._mapTitlePZ && this.elements.includes(this._mapTitlePZ)) {
            this._mapTitlePZ.updateContent(val);
          }
        });
      }
      if (this.mapZoomInBtn)  this._bindZoomBtn(this.mapZoomInBtn,   0.2);
      if (this.mapZoomOutBtn) this._bindZoomBtn(this.mapZoomOutBtn, -0.2);
      if (this.mapPreviewBtn) {
        this.mapPreviewBtn.addEventListener('click', () => this._collapsePanel());
      }
      if (this.mapCustomCoordsCheck) {
        this.mapCustomCoordsCheck.addEventListener('change', () => {
          const custom = this.mapCustomCoordsCheck.checked;
          if (this.mapCustomCoordsInput) this.mapCustomCoordsInput.hidden = !custom;
          if (custom) {
            const text = this.mapCustomCoordsInput ? this.mapCustomCoordsInput.value : '';
            if (this._mapCoordsPZ && this.elements.includes(this._mapCoordsPZ)) {
              this._mapCoordsPZ.updateContent(text);
            }
          } else {
            this._updateMapCoords();
          }
        });
      }
      if (this.mapCustomCoordsInput) {
        this.mapCustomCoordsInput.addEventListener('input', () => {
          if (this.mapCustomCoordsCheck && this.mapCustomCoordsCheck.checked) {
            const text = this.mapCustomCoordsInput.value;
            if (this._mapCoordsPZ && this.elements.includes(this._mapCoordsPZ)) {
              this._mapCoordsPZ.updateContent(text);
            }
          }
        });
      }
    }

    _bindGlobal() {
      document.addEventListener('pointermove', (e) => this._onPointerMove(e));
      document.addEventListener('pointerup',   ()  => this._onPointerUp());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal && this.modal.classList.contains('is-open')) {
          this.close();
        }
        if ((e.key === 'Delete' || e.key === 'Backspace') &&
            this.selected && document.activeElement === document.body) {
          this.removeElement(this.selected.id);
        }
      });

      window.addEventListener('resize', _debounce(() => {
        this._positionSafeZone();
        if (this.selected) this._updateHandlesOverlay();
      }, 50));

      // Browser back/forward cache herstel → pagina herladen zodat variant-opties en form-state vers zijn
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) window.location.reload();
      });
    }

    /* ---- Reset ---- */
    _reset() {
      [...this.elements].forEach(el => el.remove());
      this.elements = [];
      this.selected = null;
      if (this.handlesOverlay) this.handlesOverlay.hidden = true;

      this._uitgesnedenReady = false;

      if (this._mapInstance) {
        this._mapInstance.remove();
        this._mapInstance = null;
      }
      if (this._mapContainer && this._mapContainer.parentNode) {
        this._mapContainer.parentNode.removeChild(this._mapContainer);
        this._mapContainer = null;
      }
      this._mapTitlePZ   = null;
      this._mapCoordsPZ  = null;
      this._mapMarkers.clear();
      this._mapTabLoaded = false;
      this.mapState      = null;
      this._setPhotoTabDisabled(false);
      this._setMapTabDisabled(false);
      this._hidePhotoInfo();
      if (this._mapZoomFloat) this._mapZoomFloat.hidden = true;
      if (this._rotateBarEl)  { this._rotateBarEl.remove(); this._rotateBarEl = null; }
      if (this.mapRemoveBtn)    this.mapRemoveBtn.hidden = true;
      if (this.mapMarkerRow)    this.mapMarkerRow.hidden = true;
      if (this.mapCoordsEl)     this.mapCoordsEl.textContent = '—';
      if (this.mapTitleInput)   this.mapTitleInput.value = '';
      if (this.mapGeocoderWrap) this.mapGeocoderWrap.innerHTML = '';
      if (this.mapNoConfig)     this.mapNoConfig.hidden = true;
      if (this.mapCustomCoordsCheck) this.mapCustomCoordsCheck.checked = false;
      if (this.mapCustomCoordsInput) { this.mapCustomCoordsInput.hidden = true; this.mapCustomCoordsInput.value = ''; }
      if (this.atcDrawer)    this.atcDrawer.classList.remove('is-open');
      if (this.atcBtn)       this.atcBtn.setAttribute('aria-expanded', 'false');
      if (this.atcBtnLabel)  this.atcBtnLabel.textContent = 'In winkelwagen';
      const _atcIcon = this.atcBtn ? this.atcBtn.querySelector('svg') : null;
      if (_atcIcon) _atcIcon.style.display = '';
      this._updateControlPanel();
      if (this.confirmCheck) this.confirmCheck.checked = false;
      this.qty = 1;
      if (this.qtyVal) this.qtyVal.textContent = '1';
      this._updateAtcState();
      // Force SVG library to re-render on next tab open so color normalization is applied fresh
      this._svgLibraryLoaded = false;
      const _svgLibContainer = document.getElementById('pz-svg-library');
      if (_svgLibContainer) _svgLibContainer.innerHTML = '';
    }

    /* ---- Modal ---- */
    open(dynCfg) {
      if (!this.modal) return;

      // Apply settings from configurator button (overrides constructor defaults)
      var bgUrl           = null;
      var allowedRotations = [0];
      if (dynCfg) {
        if (dynCfg.safeZone)     this.safeZoneCfg      = dynCfg.safeZone;
        if (dynCfg.variantId)    this.variantId         = dynCfg.variantId;
        if (dynCfg.apiUrl)       this._dynApiUrl        = dynCfg.apiUrl;
        if (dynCfg.productId)    this._dynProductId     = String(dynCfg.productId);
        if (dynCfg.productTitle) this._dynProductTitle  = dynCfg.productTitle;
        if (dynCfg.backgroundUrl) bgUrl = dynCfg.backgroundUrl;
        if (dynCfg.allowedRotations && dynCfg.allowedRotations.length)
          allowedRotations = dynCfg.allowedRotations;
      }

      this._bgOrigUrl              = bgUrl;
      this._allowedRotations       = allowedRotations;
      this._bgRotIdx               = 0;
      this._origSafeZoneCfg        = this.safeZoneCfg;
      this._disabledTabsByRotation = (dynCfg && dynCfg.disabledTabsByRotation) || {};
      this._disabledTabs           = (dynCfg && Array.isArray(dynCfg.disabledTabs)) ? dynCfg.disabledTabs : [];
      this._mapShape               = (dynCfg && dynCfg.mapShape)    || null; // 'round' | 'square' | null
      this._mapSizePct             = (dynCfg && dynCfg.mapSize > 0) ? dynCfg.mapSize : null; // number 10-100 | null
      this._mapPosition            = (dynCfg && dynCfg.mapPosition) || null; // 'top-left' | 'center' | etc. | null
      this._uitgesnedenMode        = !!(dynCfg && dynCfg.priorityView === 'uitsneden');
      this._uitgesnedenToken       = this._uitgesnedenMode ? (dynCfg.mapboxToken || '') : '';
      this._uitgesnedenStyleUrl    = this._uitgesnedenMode ? (dynCfg.mapboxStyleUrl || '') : '';

      if (this.bgRotateBar) this.bgRotateBar.hidden = true; // vervangen door floating knoppen op preview

      this._reset();
      this._applyDisabledTabs();
      this._updateMapTabState();
      this._initRotateButtons(allowedRotations);

      // Activeer de juiste begintab op basis van priority_view uit de optie-config
      var startTab = (dynCfg && dynCfg.priorityView === 'stadskaart') ? 'map'
                   : this._uitgesnedenMode ? 'uitsneden'
                   : 'text';
      if (this._disabledTabs.includes(startTab)) {
        startTab = ['text', 'photo', 'svg', 'map'].find(t => !this._disabledTabs.includes(t)) || 'text';
      }
      this._doSwitchTab(startTab);

      // Uitsneden modus: toon kaart-frame preview ipv product-afbeelding
      var _ukPreview  = document.getElementById('pz-uk-preview');
      var _imgWrapper = document.getElementById('pz-image-wrapper');
      var _prevHint   = this.modal && this.modal.querySelector('.pz-preview-hint');
      if (this._uitgesnedenMode) {
        if (_imgWrapper) _imgWrapper.hidden = true;
        if (_ukPreview)  _ukPreview.hidden  = false;
        if (_prevHint)   _prevHint.hidden   = true;
        this._uitgesnedenReady = true;
        this._updateAtcState();
      } else {
        if (_imgWrapper) _imgWrapper.hidden = false;
        if (_ukPreview)  _ukPreview.hidden  = true;
        if (_prevHint)   _prevHint.hidden   = false;
      }

      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      if (this.triggerBtn) this.triggerBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';

      // Laad alle fonts direct bij openen zodat ze beschikbaar zijn in elke view
      this._loadFonts();

      // Prevent iOS Safari pinch-to-zoom inside the modal
      if (!this._preventZoom) {
        this._preventZoom = (e) => { if (e.touches.length > 1) e.preventDefault(); };
        this.modal.addEventListener('touchmove', this._preventZoom, { passive: false });
      }

      this._expandPanel();

      // Uitsneden kaart: double-rAF + setTimeout zorgt dat flex-layout volledig is
      // berekend vóórdat Mapbox de container meet (clientWidth/clientHeight)
      if (this._uitgesnedenMode) {
        var _uitSelf = this;
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            setTimeout(function () {
              _uitSelf._initUitgesnedenMap(_uitSelf._uitgesnedenToken, _uitSelf._uitgesnedenStyleUrl);
            }, 0);
          });
        });
      }

      // Load background — rotate via offscreen canvas when needed so all
      // existing size/safezone calculations work without any coordinate changes.
      var self = this;
      var initDeg = allowedRotations[0] || 0;
      if (bgUrl && this.productImage) {
        this.safeZoneCfg = _rotateSafezone(this._origSafeZoneCfg, initDeg);
        _rotateBg(bgUrl, initDeg).then(function (src) {
          if (!self.productImage) return;
          self.productImage.src = src;
          if (self.productImage.complete) {
            self._positionSafeZone();
          } else {
            self.productImage.addEventListener('load', function () { self._positionSafeZone(); }, { once: true });
          }
        });
      } else if (this.productImage && this.productImage.complete) {
        this._positionSafeZone();
      } else if (this.productImage) {
        this.productImage.addEventListener('load', () => this._positionSafeZone(), { once: true });
      }

      this._showTutorial();

      // ResizeObserver: re-position safe zone whenever image renders at a different size
      if (window.ResizeObserver && this.productImage && !this._resizeObs) {
        this._resizeObs = new ResizeObserver(() => {
          if (this.modal && this.modal.classList.contains('is-open')) {
            this._positionSafeZone();
            if (this.selected) this._updateHandlesOverlay();
          }
        });
        this._resizeObs.observe(this.productImage);
      }
    }

    close() {
      if (!this.modal) return;
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      if (this.triggerBtn) {
        this.triggerBtn.setAttribute('aria-expanded', 'false');
        this.triggerBtn.focus();
      }
      document.body.style.overflow = '';

      if (this._preventZoom) {
        this.modal.removeEventListener('touchmove', this._preventZoom);
        this._preventZoom = null;
      }
    }

    /* ---- Background rotation ---- */
    _confirmBgRotate(idx) {
      if (this.elements.length === 0 && !this._mapInstance) {
        this._applyBgRotation(idx);
        return;
      }
      const overlay = document.createElement('div');
      overlay.className = 'pz-confirm-overlay';
      overlay.innerHTML =
        '<div class="pz-confirm-dialog">' +
        '<p class="pz-confirm-dialog-msg">Als je het product draait, wordt je huidige ontwerp verwijderd. Wil je doorgaan?</p>' +
        '<div class="pz-confirm-dialog-actions">' +
        '<button type="button" class="pz-confirm-dialog-cancel">Nee, behoud ontwerp</button>' +
        '<button type="button" class="pz-confirm-dialog-ok">Ja, verwijder ontwerp</button>' +
        '</div></div>';
      const container = this.modal.querySelector('.personalizer-modal-content') || this.modal;
      container.appendChild(overlay);
      overlay.querySelector('.pz-confirm-dialog-cancel').addEventListener('click', () => overlay.remove());
      overlay.querySelector('.pz-confirm-dialog-ok').addEventListener('click', () => {
        overlay.remove();
        this._reset();
        this._applyBgRotation(idx);
      });
    }

    _applyBgRotation(idx) {
      if (!this._bgOrigUrl || !this.productImage) return;
      var self = this;
      this._bgRotIdx = idx;
      this._updateMapTabState();
      var degrees = this._allowedRotations[idx] || 0;
      this.safeZoneCfg = _rotateSafezone(this._origSafeZoneCfg, degrees);
      this._initRotateButtons(this._allowedRotations);
      _rotateBg(this._bgOrigUrl, degrees).then(function (src) {
        if (!self.productImage) return;
        self.productImage.src = src;
        if (self.productImage.complete) {
          self._positionSafeZone();
        } else {
          self.productImage.addEventListener('load', function () { self._positionSafeZone(); }, { once: true });
        }
      });
    }

    /* ---- Floating rotate buttons (op de preview-afbeelding, bereikbaar op mobiel) ---- */
    _initRotateButtons(allowedRotations) {
      if (this._rotateBarEl) { this._rotateBarEl.remove(); this._rotateBarEl = null; }
      if (!this.imgWrapper || !allowedRotations || allowedRotations.length <= 1) return;

      const ccwSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><path d="M2.5 2v6h6"/><path d="M2.66 15.57a10 10 0 1 0 .57-8.38"/></svg>';
      const cwSvg  = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><path d="M21.5 2v6h-6"/><path d="M21.34 15.57a10 10 0 1 1-.57-8.38"/></svg>';

      const bar = document.createElement('div');
      bar.className = 'pz-bg-rotate-bar';
      bar.setAttribute('aria-label', 'Product draaien');

      const ccwBtn = document.createElement('button');
      ccwBtn.type = 'button';
      ccwBtn.className = 'pz-bg-rotate-btn';
      ccwBtn.setAttribute('aria-label', 'Tegen de klok in draaien');
      ccwBtn.innerHTML = ccwSvg;
      ccwBtn.addEventListener('click', () => this._confirmBgRotate((this._bgRotIdx - 1 + this._allowedRotations.length) % this._allowedRotations.length));

      const hint = document.createElement('span');
      hint.className = 'pz-bg-rotate-hint';
      hint.textContent = 'Draaien';

      const cwBtn = document.createElement('button');
      cwBtn.type = 'button';
      cwBtn.className = 'pz-bg-rotate-btn';
      cwBtn.setAttribute('aria-label', 'Met de klok mee draaien');
      cwBtn.innerHTML = cwSvg;
      cwBtn.addEventListener('click', () => this._confirmBgRotate((this._bgRotIdx + 1) % this._allowedRotations.length));

      bar.appendChild(ccwBtn);
      bar.appendChild(hint);
      bar.appendChild(cwBtn);

      this.imgWrapper.appendChild(bar);
      this._rotateBarEl = bar;
    }

    /* ---- Safe zone ---- */
    _positionSafeZone() {
      if (!this.safeZone || !this.productImage || !this.imgWrapper) return;
      const cfg = this.safeZoneCfg;
      const r = _imgContentRect(this.productImage);
      if (!r.width || !r.height) return;
      this.safeZone.style.left   = (r.left + Math.round(cfg.x      / 100 * r.width))  + 'px';
      this.safeZone.style.top    = (r.top  + Math.round(cfg.y      / 100 * r.height)) + 'px';
      this.safeZone.style.width  = Math.round(cfg.width  / 100 * r.width)  + 'px';
      this.safeZone.style.height = Math.round(cfg.height / 100 * r.height) + 'px';

      // Position rotate bar (position:fixed) just above the product image content.
      // Use viewport coordinates so it always clears the modal header stacking context.
      if (this._rotateBarEl) {
        const barEl = this._rotateBarEl;
        const wrapperRect = this.imgWrapper.getBoundingClientRect();
        requestAnimationFrame(() => {
          if (!barEl.parentNode) return;
          const barH   = barEl.offsetHeight || 44;
          const barW   = barEl.offsetWidth  || 120;
          const top    = Math.max(4, wrapperRect.top + r.top - barH - 6);
          const left   = wrapperRect.left + wrapperRect.width / 2 - barW / 2;
          barEl.style.top       = top + 'px';
          barEl.style.left      = left + 'px';
          barEl.style.transform = 'none';
          barEl.style.bottom    = 'auto';
        });
      }

      // Create content clip layer once
      if (!this.safeContent) {
        this.safeContent = document.createElement('div');
        this.safeContent.className = 'pz-safe-content';
        this.safeZone.appendChild(this.safeContent);
      }

      // Create handles overlay once
      if (!this.handlesOverlay) {
        this.handlesOverlay = document.createElement('div');
        this.handlesOverlay.className = 'pz-handles-overlay';
        this.handlesOverlay.setAttribute('aria-hidden', 'true');
        this.handlesOverlay.hidden = true;

        const DEFS = [
          {
            cls: 'pz-overlay-rotate',
            title: 'Draaien',
            svg: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
               + '<path d="M2.5 8A5.5 5.5 0 1 0 4 4.5"/><polyline points="1,2.5 4,5.5 6.5,3"/></svg>'
          },
          {
            cls: 'pz-overlay-scale',
            title: 'Schalen',
            svg: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
               + '<polyline points="1,9 1,15 7,15"/><polyline points="15,7 15,1 9,1"/><line x1="1" y1="15" x2="15" y2="1"/></svg>'
          },
          {
            cls: 'pz-overlay-delete',
            title: 'Verwijderen',
            svg: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">'
               + '<line x1="3" y1="3" x2="13" y2="13"/><line x1="13" y1="3" x2="3" y2="13"/></svg>'
          }
        ];

        DEFS.forEach(d => {
          const btn = document.createElement('div');
          btn.className = 'pz-overlay-handle ' + d.cls;
          btn.setAttribute('title', d.title);
          btn.setAttribute('role', 'button');
          btn.setAttribute('tabindex', '0');
          btn.innerHTML = d.svg;
          this.handlesOverlay.appendChild(btn);
        });

        // Bind handle interactions — always uses current this.selected at event time
        this.handlesOverlay.querySelector('.pz-overlay-rotate')
          .addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            if (this.selected) this.startInteraction('rotate', this.selected, e);
          });
        this.handlesOverlay.querySelector('.pz-overlay-scale')
          .addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            if (this.selected) this.startInteraction('scale', this.selected, e);
          });
        this.handlesOverlay.querySelector('.pz-overlay-delete')
          .addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            if (this.selected) this.removeElement(this.selected.id);
          });

        this.safeZone.appendChild(this.handlesOverlay);
      }

      // Snap guide lines — appended directly to safe zone (not content)
      if (!this.safeZone.querySelector('.pz-guide-h')) {
        ['h', 'v'].forEach(dir => {
          const g = document.createElement('div');
          g.className = 'pz-guide-' + dir;
          g.setAttribute('aria-hidden', 'true');
          this.safeZone.appendChild(g);
        });
      }

      const szW = this.safeZone.offsetWidth;
      const szH = this.safeZone.offsetHeight;
      this.elements.forEach(el => {
        el.x = el.xFrac * szW;
        el.y = el.yFrac * szH;
        el._applyTransform();
      });
      if (this._mapInstance) setTimeout(() => this._mapInstance && this._mapInstance.resize(), 50);
    }

    /* ---- Handle overlay positioning (called on every transform change) ---- */
    _updateHandlesOverlay() {
      if (!this.handlesOverlay) return;
      const el = this.selected;
      if (!el || !el.el) {
        this.handlesOverlay.hidden = true;
        return;
      }

      const content = el.el.querySelector('.pz-element-content');
      const nw = Math.max((content ? content.offsetWidth  : el.el.offsetWidth),  10);
      const nh = Math.max((content ? content.offsetHeight : el.el.offsetHeight), 10);

      // For photo/svg, el.scale is design-space; convert to CSS display scale.
      // For text, el.scale is already a CSS multiplier.
      let cssScaleEl = el.scale;
      if ((el.type === 'photo' || el.type === 'svg') && this.safeZone && this.safeZone.offsetWidth) {
        cssScaleEl = el.scale * this.safeZone.offsetWidth / this._designSize().w;
      }

      // Visual half-dimensions after scale
      const hw = nw * cssScaleEl / 2;
      const hh = nh * cssScaleEl / 2;

      // Rotation matrix (CSS clockwise rotation)
      const rad = el.rotation * Math.PI / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);

      const vCY = el.y;

      // Rotate handle: directly above center — local offset (0, -(hh+38))
      const ROPE = hh + 38;
      const rotX   = el.x + ROPE * sin;
      const rotY   = vCY - ROPE * cos;

      // Scale handle: bottom-right corner — local offset (hw, hh)
      const scaleX = el.x + hw * cos - hh * sin;
      const scaleY = vCY + hw * sin + hh * cos;

      // Delete handle: top-right corner — local offset (hw, -hh)
      const deleteX = el.x + hw * cos + hh * sin;
      const deleteY = vCY + hw * sin - hh * cos;

      const rotH   = this.handlesOverlay.querySelector('.pz-overlay-rotate');
      const scaleH = this.handlesOverlay.querySelector('.pz-overlay-scale');
      const delH   = this.handlesOverlay.querySelector('.pz-overlay-delete');

      if (rotH) {
        rotH.style.left = rotX   + 'px';
        rotH.style.top  = rotY   + 'px';
      }
      if (scaleH) {
        scaleH.style.left = scaleX + 'px';
        scaleH.style.top  = scaleY + 'px';
      }
      if (delH) {
        delH.style.left = deleteX + 'px';
        delH.style.top  = deleteY + 'px';
      }

      this.handlesOverlay.hidden = false;
    }

    /* ---- Tutorial ---- */
    _showTutorial() { if (this.tutorial) this.tutorial.classList.add('is-visible'); }
    _hideTutorial() { if (this.tutorial) this.tutorial.classList.remove('is-visible'); }

    _showPhotoInfo() {
      this._hideTutorial();
      if (!this._photoInfoEl) {
        const el = document.createElement('div');
        el.className = 'pz-tutorial';
        el.setAttribute('role', 'dialog');
        el.setAttribute('aria-modal', 'true');
        el.innerHTML =
          '<div class="pz-tutorial-card">' +
          '<h3 class="pz-tutorial-title">Waarom ziet mijn foto er korrelig uit?</h3>' +
          '<ol class="pz-tutorial-steps">' +
          '<li><span class="pz-tut-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></span>' +
          '<div><strong>Dit is een gravure-preview</strong><span>Zwart = gegraveerd hout, wit = onbewerkt hout</span></div></li>' +
          '<li><span class="pz-tut-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg></span>' +
          '<div><strong>Korreligheid = meer detail</strong><span>Zonder korreligheid zou een grijze foto als één zwart vlak worden gegraveerd — het puntpatroon maakt lichte en donkere tinten zichtbaar</span></div></li>' +
          '<li><span class="pz-tut-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>' +
          '<div><strong>Op hout ziet het er mooier uit</strong><span>Het eindresultaat oogt warmer en natuurlijker dan de preview doet vermoeden</span></div></li>' +
          '<li><span class="pz-tut-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></svg></span>' +
          '<div><strong>Houd de foto zo groot mogelijk</strong><span>Hoe groter de foto op het product, hoe meer detail zichtbaar is in de gravure — maak de foto niet kleiner dan nodig</span></div></li>' +
          '</ol>' +
          '<button type="button" class="pz-tutorial-btn">Begrepen! <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg></button>' +
          '</div>';
        el.querySelector('.pz-tutorial-btn').addEventListener('click', () => this._hidePhotoInfo());
        document.body.appendChild(el);
        this._photoInfoEl = el;
      }
      this._photoInfoEl.classList.add('is-visible');
    }
    _hidePhotoInfo() { if (this._photoInfoEl) this._photoInfoEl.classList.remove('is-visible'); }

    /* ---- Panel collapse (mobile) ---- */
    _isMobile() { return window.matchMedia('(max-width: 820px)').matches; }

    _expandPanel() {
      if (!this.panelBody) return;
      // Deselect active element so the canvas is clean when picking a new item
      this.deselectAll();
      this.panelBody.classList.remove('is-collapsed');
      if (this.panelToggle) this.panelToggle.setAttribute('aria-expanded', 'true');
      // Close ATC drawer when opening the main panel
      if (this.atcDrawer) this.atcDrawer.classList.remove('is-open');
      // Safe zone may need repositioning after layout changes
      requestAnimationFrame(() => this._positionSafeZone());
    }

    _collapsePanel() {
      if (!this.panelBody) return;
      this.panelBody.classList.add('is-collapsed');
      if (this.panelToggle) this.panelToggle.setAttribute('aria-expanded', 'false');
      this._updateMobileBar();
      requestAnimationFrame(() => this._positionSafeZone());
    }

    async _toggleAtcDrawer() {
      if (!this.atcDrawer) return;
      const opening = !this.atcDrawer.classList.contains('is-open');
      this.atcDrawer.classList.toggle('is-open', opening);
      if (this.atcBtn) this.atcBtn.setAttribute('aria-expanded', String(opening));
      if (this.atcBtnLabel) this.atcBtnLabel.textContent = opening ? 'Sluiten' : 'In winkelwagen';
      const atcIcon = this.atcBtn ? this.atcBtn.querySelector('svg') : null;
      if (atcIcon) atcIcon.style.display = opening ? 'none' : '';

      if (opening) {
        const wrap = document.getElementById('pz-atc-preview-wrap');
        const img  = document.getElementById('pz-atc-preview-img');
        if (wrap && img) {
          wrap.hidden = false;
          img.src = '';
          img.classList.add('is-loading');
          const dataUrl = await this._generatePreview();
          img.classList.remove('is-loading');
          if (dataUrl) img.src = dataUrl;
        }
      }
    }

    _togglePanel() {
      if (!this.panelBody) return;
      if (this.panelBody.classList.contains('is-collapsed')) {
        this._expandPanel();
      } else {
        this._collapsePanel();
      }
    }

    _updateMobileBar() {
      if (!this.mobileBar) return;
      const collapsed = this.panelBody && this.panelBody.classList.contains('is-collapsed');
      const show      = this._isMobile() && !!this.selected && collapsed;
      this.mobileBar.hidden = !show;
      if (show && this.selected) {
        const scaleMax = this.selected.type === 'svg' ? 20 : 6;
        if (this.mbScale)    { this.mbScale.max = scaleMax; this.mbScale.value = this.selected.scale; }
        if (this.mbRotation) this.mbRotation.value = this.selected.rotation;
      }
    }

    /* ---- Guide lines ---- */
    _showGuides(h, v) {
      if (!this.safeZone) return;
      const gh = this.safeZone.querySelector('.pz-guide-h');
      const gv = this.safeZone.querySelector('.pz-guide-v');
      if (gh) gh.classList.toggle('is-visible', h);
      if (gv) gv.classList.toggle('is-visible', v);
    }

    /* ---- Tabs ---- */
    _switchTab(name) {
      const targetBtn = [...this.tabBtns].find(b => b.dataset.tab === name);
      if (targetBtn && targetBtn.classList.contains('pz-tab--disabled')) {
        this._showTabMessage(targetBtn.getAttribute('title') || 'Dit tabblad is momenteel niet beschikbaar.');
        return;
      }
      // Warn when switching to map/photo with an existing design.
      // Exception: switching back to map tab while the map is already active is fine —
      // the user is just navigating between tabs and text can coexist with the map.
      if (name === 'map' || name === 'photo') {
        const alreadyActive = targetBtn && targetBtn.classList.contains('is-active');
        if (!alreadyActive) {
          const userEls = this.elements.filter(el => el !== this._mapTitlePZ && el !== this._mapCoordsPZ && !this._mapMarkers.has(el));
          const skipWarning = name === 'map' && !!this._mapInstance;
          if (userEls.length > 0 && !skipWarning) {
            this._confirmTabSwitch(name, () => {
              userEls.forEach(el => {
                const idx = this.elements.indexOf(el);
                if (idx !== -1) { el.remove(); this.elements.splice(idx, 1); }
              });
              this.selected = null;
              if (this.handlesOverlay) this.handlesOverlay.hidden = true;
              this._updateControlPanel();
              this._updateMobileBar();
              this._updateAtcState();
              this._doSwitchTab(name);
            });
            return;
          }
        }
      }
      this._doSwitchTab(name);
    }

    _doSwitchTab(name) {
      this.tabBtns.forEach(btn => {
        const active = btn.dataset.tab === name;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active);
      });
      this.tabPanels.forEach(panel => {
        const active = panel.id === 'tab-panel-' + name;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
      if (name === 'svg')  this._loadSvgLibrary();
      if (name === 'text') { this._loadFonts(); this._updateTextTabWarning(); }
      if (name === 'map')  this._loadMapTab();
    }

    _confirmTabSwitch(targetTab, onConfirm) {
      const tabName = targetTab === 'map' ? 'stadskaart' : 'foto';
      const overlay = document.createElement('div');
      overlay.className = 'pz-confirm-overlay';
      overlay.innerHTML =
        `<div class="pz-confirm-dialog">` +
        `<p class="pz-confirm-dialog-msg">Als je verder gaat met een ${tabName} wordt je huidige design verwijderd. Wil je doorgaan?</p>` +
        `<div class="pz-confirm-dialog-actions">` +
        `<button type="button" class="pz-confirm-dialog-cancel">Annuleren</button>` +
        `<button type="button" class="pz-confirm-dialog-ok">Ja, ik weet het zeker</button>` +
        `</div></div>`;
      const container = this.modal.querySelector('.personalizer-modal-content') || this.modal;
      container.appendChild(overlay);
      overlay.querySelector('.pz-confirm-dialog-cancel').addEventListener('click', () => overlay.remove());
      overlay.querySelector('.pz-confirm-dialog-ok').addEventListener('click', () => {
        overlay.remove();
        onConfirm();
      });
    }

    _showTabMessage(msg) {
      let el = this._tabMsgEl;
      if (!el) {
        el = document.createElement('div');
        el.className = 'pz-tab-msg';
        el.setAttribute('role', 'alert');
        el.setAttribute('aria-live', 'polite');
        const tabs = document.querySelector('.pz-tabs');
        if (tabs && tabs.parentNode) tabs.parentNode.insertBefore(el, tabs.nextSibling);
        this._tabMsgEl = el;
      }
      el.textContent = msg;
      el.classList.add('is-visible');
      clearTimeout(this._tabMsgTimer);
      this._tabMsgTimer = setTimeout(() => el.classList.remove('is-visible'), 3500);
    }

    /* ---- Fonts (dynamisch via API) ---- */
    async _loadFonts() {
      if (this._fontsLoaded) return;

      const apiUrl = this._dynApiUrl || (window.personalizerConfig || {}).apiUrl;
      if (!apiUrl || !this.fontSelect) return;

      try {
        const res   = await fetch(apiUrl + 'public/fonts');
        if (!res.ok) throw new Error(res.status);
        const fonts = await res.json();

        // Laad elk font in de browser via FontFace API
        await Promise.all(fonts.map(async f => {
          const primaryName = f.family.split(',')[0].replace(/['"]/g, '').trim();
          try {
            const face = new FontFace(primaryName, 'url(' + f.public_url + ')');
            await face.load();
            document.fonts.add(face);
          } catch (e) {
            console.warn('[Personalizer] Font laden mislukt:', primaryName, e);
          }
        }));

        // Vul de select met de geladen fonts
        this.fontSelect.innerHTML = '';
        fonts.forEach(f => {
          const opt = document.createElement('option');
          opt.value           = f.family;
          opt.textContent     = f.name;
          opt.style.fontFamily = f.family;
          this.fontSelect.appendChild(opt);
        });
        this.fontSelect.disabled = false;

        this._fontsLoaded = true;

        // Ververs font-preview met het eerste geladen font
        this.fontSelect.dispatchEvent(new Event('change'));

      } catch (err) {
        console.error('[Personalizer] Fonts laden mislukt:', err);
        this.fontSelect.innerHTML = '<option value="">Fonts niet beschikbaar</option>';
      }
    }

    /* ---- SVG library (dynamisch via API, accordion per categorie) ---- */
    async _loadSvgLibrary() {
      if (this._svgLibraryLoaded) return;

      const container = document.getElementById('pz-svg-library');
      if (!container) return;

      const apiUrl = this._dynApiUrl || (window.personalizerConfig || {}).apiUrl;
      if (!apiUrl) return;

      try {
        const res        = await fetch(apiUrl + 'public/svgs');
        if (!res.ok) throw new Error(res.status);
        const categories = await res.json();

        container.innerHTML = '';

        for (const cat of categories) {
          if (!cat.svgs || cat.svgs.length === 0) continue;

          // Categorie-header als toggle knop
          const toggle = document.createElement('button');
          toggle.type      = 'button';
          toggle.className = 'pz-svg-cat-toggle';
          toggle.setAttribute('aria-expanded', 'false');
          toggle.innerHTML =
            '<span class="pz-svg-cat-name">' + _esc(cat.name) + '</span>' +
            '<span class="pz-svg-cat-count">' + cat.svgs.length + '</span>' +
            '<svg class="pz-svg-cat-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="4 6 8 10 12 6"/></svg>';

          // Grid begint dicht
          const grid = document.createElement('div');
          grid.className = 'pz-svg-grid';
          grid.hidden    = true;

          let loaded = false;

          toggle.addEventListener('click', () => {
            const opening = grid.hidden;
            grid.hidden   = !opening;
            toggle.setAttribute('aria-expanded', String(opening));

            // Render placeholders op eerste open; observer triggert daarna per item
            if (opening && !loaded) {
              loaded = true;

              const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                  if (!entry.isIntersecting) return;
                  observer.unobserve(entry.target);
                  const btn = entry.target;
                  const url = btn.dataset.svgUrl;
                  if (!url) return;
                  fetch(url)
                    .then(r => r.text())
                    .catch(() => null)
                    .then(svgContent => {
                      if (!svgContent) { btn.remove(); return; }
                      btn.innerHTML = _sanitizeSvgContent(svgContent);
                      btn.classList.remove('pz-svg-item--loading');
                      btn.addEventListener('click', () => this._addSVG(btn));
                    });
                });
              }, { root: grid.closest('.pz-tab-panels'), rootMargin: '120px' });

              cat.svgs.forEach(s => {
                const btn = document.createElement('button');
                btn.type      = 'button';
                btn.className = 'pz-svg-item pz-svg-item--loading';
                btn.setAttribute('aria-label', s.name);
                btn.setAttribute('title', s.name);
                btn.dataset.svgUrl = s.public_url;
                grid.appendChild(btn);
                observer.observe(btn);
              });
            }
          });

          container.appendChild(toggle);
          container.appendChild(grid);
        }

        if (!container.children.length) {
          container.innerHTML = '<p class="pz-svg-empty">Geen symbolen beschikbaar.</p>';
        }

        this._svgLibraryLoaded = true;

      } catch (err) {
        console.error('[Personalizer] SVG library laden mislukt:', err);
        container.innerHTML = '<p class="pz-svg-error">Symbolen konden niet worden geladen.</p>';
      }
    }

    /* ---- Stadskaart ---- */
    async _loadMapTab() {
      if (this._mapTabLoaded) {
        if (this._mapInstance) setTimeout(() => this._mapInstance.resize(), 100);
        return;
      }

      // Prevent combining with photo upload
      const hasPhoto = this.elements.some(e => e.type === 'photo');
      if (hasPhoto) {
        if (this.mapGeocoderWrap) this.mapGeocoderWrap.hidden = true;
        if (this.mapNoConfig) {
          this.mapNoConfig.textContent = 'Verwijder eerst de foto om een stadskaart te gebruiken.';
          this.mapNoConfig.hidden = false;
        }
        return;
      }

      // Fetch Mapbox settings from API
      const apiUrl = this._dynApiUrl || (window.personalizerConfig || {}).apiUrl;
      let mbCfg;
      try {
        const res = await fetch(apiUrl + 'public/mapbox-settings');
        if (!res.ok) throw new Error(res.status);
        mbCfg = await res.json();
      } catch (err) {
        console.error('[Personalizer] Mapbox settings laden mislukt:', err);
        if (this.mapNoConfig) {
          this.mapNoConfig.textContent = 'Stadskaart niet beschikbaar voor dit product.';
          this.mapNoConfig.hidden = false;
        }
        return;
      }

      if (!mbCfg || !mbCfg.accessToken) {
        if (this.mapNoConfig) this.mapNoConfig.hidden = false;
        return;
      }

      if (this.mapGeocoderWrap) this.mapGeocoderWrap.hidden = false;
      if (this.mapNoConfig) this.mapNoConfig.hidden = true;

      await this._loadMapboxScripts();

      // Build map container in safe zone
      this._mapContainer = document.createElement('div');
      this._mapContainer.className = 'pz-map-zone';
      var useShape = this._mapShape || mbCfg.shape || 'square';
      if (useShape === 'round') this._mapContainer.classList.add('pz-map-zone--round');
      if (this._mapSizePct || this._mapPosition) {
        var sizePct = Math.max(10, Math.min(100, this._mapSizePct || 90));
        var offsets = _getMapOffsets(this._mapPosition, sizePct);
        this._mapContainer.style.width = sizePct      + '%';
        this._mapContainer.style.left  = offsets.left + '%';
        this._mapContainer.style.top   = offsets.top  + '%';
      }
      if (this.safeContent) this.safeContent.appendChild(this._mapContainer);

      // Init Mapbox
      window.mapboxgl.accessToken = mbCfg.accessToken;
      const _mbStyleUrl = mbCfg.styleUrl || 'mapbox://styles/mapbox/streets-v12';
      const _mbDoInit = (styleInput) => {
        this._mapInstance = new window.mapboxgl.Map({
          container:            this._mapContainer,
          style:                styleInput,
          center:               [mbCfg.defaultLng || 4.9041, mbCfg.defaultLat || 52.3676],
          zoom:                 mbCfg.zoom || 12,
          preserveDrawingBuffer: true,
          attributionControl:   false
        });

        this.mapState = {
          center: { lng: mbCfg.defaultLng || 4.9041, lat: mbCfg.defaultLat || 52.3676 },
          zoom:   mbCfg.zoom || 12,
          title:  mbCfg.defaultTitle || '',
          shape:  mbCfg.shape || 'square'
        };
        if (mbCfg.defaultTitle && this.mapTitleInput) {
          this.mapTitleInput.value = mbCfg.defaultTitle;
        }

        this._mapInstance.on('moveend', () => this._updateMapCoords());
        this._mapInstance.on('load', () => {
          // Witte achtergrond transparant zodat het hout zichtbaar is door de kaart heen.
          if (this._mapInstance.getLayer('background')) {
            this._mapInstance.setPaintProperty('background', 'background-opacity', 0);
          }
          this._updateMapCoords();
          setTimeout(() => this._mapInstance && this._mapInstance.resize(), 50);
        });

        this._initGeocoder(mbCfg);
        this._createMapTextElements(mbCfg);
        this._setPhotoTabDisabled(true);
        if (this.mapRemoveBtn) this.mapRemoveBtn.hidden = false;
        if (this.mapMarkerRow) this.mapMarkerRow.hidden = false;

        // Floating zoom buttons on the preview (useful especially on mobile)
        if (!this._mapZoomFloat) {
          const fz = document.createElement('div');
          fz.className = 'pz-map-zoom-float';
          fz.innerHTML =
            '<button class="pz-map-fzoom-btn" type="button" aria-label="Inzoomen" data-fz="in">+</button>' +
            '<button class="pz-map-fzoom-btn" type="button" aria-label="Uitzoomen" data-fz="out">−</button>';
          this._bindZoomBtn(fz.querySelector('[data-fz="in"]'),   0.2);
          this._bindZoomBtn(fz.querySelector('[data-fz="out"]'), -0.2);
          if (this.safeZone) this.safeZone.appendChild(fz);
          this._mapZoomFloat = fz;
        }
        this._mapZoomFloat.hidden = false;

        this._mapTabLoaded = true;
        this._updateAtcState();
      };

      _mbDoInit(_mbStyleUrl);
    }

    async _loadMapboxScripts() {
      const loadCss = (href) => {
        if (document.querySelector('link[href="' + href + '"]')) return;
        const l = document.createElement('link');
        l.rel = 'stylesheet'; l.href = href;
        document.head.appendChild(l);
      };
      const loadScript = (src) => new Promise((resolve, reject) => {
        if (document.querySelector('script[src="' + src + '"]')) { resolve(); return; }
        const s = document.createElement('script');
        s.src = src; s.onload = resolve; s.onerror = reject;
        document.head.appendChild(s);
      });

      loadCss('https://api.mapbox.com/mapbox-gl-js/v3.6.0/mapbox-gl.css');
      loadCss('https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-geocoder/v5.0.0/mapbox-gl-geocoder.css');
      if (!window.mapboxgl) {
        await loadScript('https://api.mapbox.com/mapbox-gl-js/v3.6.0/mapbox-gl.js');
      }
      if (!window.MapboxGeocoder) {
        await loadScript('https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-geocoder/v5.0.0/mapbox-gl-geocoder.min.js');
      }
    }

    _createMapTextElements(mbCfg) {
      if (!this.safeContent || !this.safeZone) return;
      const szH = this.safeZone.offsetHeight || 400;
      const szW = this.safeZone.offsetWidth  || 300;

      // Map bottom: top offset px + map height px (aspect-ratio 1:1 so height = width)
      const _mapSzPct   = this._mapSizePct || 90;
      const _offsets    = _getMapOffsets(this._mapPosition, _mapSzPct);
      const mapBottomPx = szH * (_offsets.top / 100) + szW * (_mapSzPct / 100);

      // Title PZElement
      this._mapTitlePZ = new PZElement('text', mbCfg.defaultTitle || '', {
        fontFamily:   "'Josefin Sans', sans-serif",
        fontSizeFrac: 0.075,
        scale:        1
      });
      this._mapTitlePZ.mount(this.safeContent);
      const titleY = Math.min(mapBottomPx + szH * 0.12, szH * 0.95);
      this._mapTitlePZ.x     = szW / 2;
      this._mapTitlePZ.y     = titleY;
      this._mapTitlePZ.xFrac = 0.5;
      this._mapTitlePZ.yFrac = szH > 0 ? titleY / szH : 0.88;
      this._mapTitlePZ._applyTransform();
      this.elements.push(this._mapTitlePZ);

      // Coords PZElement — iets kleiner dan titel
      this._mapCoordsPZ = new PZElement('text', '', {
        fontFamily:   "'Josefin Sans', sans-serif",
        fontSizeFrac: 0.055,
        scale:        1
      });
      this._mapCoordsPZ.mount(this.safeContent);
      const coordsY = Math.min(titleY + szH * 0.10, szH * 0.99);
      this._mapCoordsPZ.x     = szW / 2;
      this._mapCoordsPZ.y     = coordsY;
      this._mapCoordsPZ.xFrac = 0.5;
      this._mapCoordsPZ.yFrac = szH > 0 ? coordsY / szH : 0.97;
      this._mapCoordsPZ._applyTransform();
      this.elements.push(this._mapCoordsPZ);
    }

    _initGeocoder(mbCfg) {
      const container = this.mapGeocoderWrap;
      if (!container || !window.MapboxGeocoder || !window.mapboxgl) return;

      const geocoder = new window.MapboxGeocoder({
        accessToken: mbCfg.accessToken,
        mapboxgl:    window.mapboxgl,
        placeholder: 'Zoek een stad…',
        language:    'nl',
        types:       'place,locality,neighborhood',
        proximity:   { longitude: 5.2913, latitude: 52.1326 },
        marker:      false,
        flyTo:       false
      });

      geocoder.on('result', (e) => {
        const center = e.result.center; // [lng, lat]
        if (this._mapInstance) {
          this._mapInstance.flyTo({ center, zoom: mbCfg.zoom || 12, duration: 1500 });
        }
        const title = e.result.text || (e.result.place_name || '').split(',')[0];
        if (this.mapTitleInput) {
          this.mapTitleInput.value = title;
          if (this.mapState) this.mapState.title = title;
        }
        if (this._mapTitlePZ && this.elements.includes(this._mapTitlePZ)) {
          this._mapTitlePZ.updateContent(title);
        }
      });

      geocoder.addTo(container);
    }

    _updateMapCoords() {
      if (!this._mapInstance) return;
      const c = this._mapInstance.getCenter();
      const absLat = Math.abs(c.lat).toFixed(4);
      const absLng = Math.abs(c.lng).toFixed(4);
      const ns = c.lat >= 0 ? 'N' : 'S';
      const ew = c.lng >= 0 ? 'E' : 'W';
      const text = absLat + '°' + ns + ', ' + absLng + '°' + ew;
      if (this.mapCoordsEl) this.mapCoordsEl.textContent = text;
      const inCustomMode = this.mapCustomCoordsCheck && this.mapCustomCoordsCheck.checked;
      if (!inCustomMode && this._mapCoordsPZ && this.elements.includes(this._mapCoordsPZ)) {
        this._mapCoordsPZ.updateContent(text);
      }
      if (this.mapState) {
        this.mapState.center = { lng: c.lng, lat: c.lat };
        this.mapState.zoom   = this._mapInstance.getZoom();
      }
    }

    _removeMap() {
      if (this._mapInstance) {
        this._mapInstance.remove();
        this._mapInstance = null;
      }
      if (this._mapContainer && this._mapContainer.parentNode) {
        this._mapContainer.parentNode.removeChild(this._mapContainer);
        this._mapContainer = null;
      }
      // Remove title, coords and all map marker PZElements
      [this._mapTitlePZ, this._mapCoordsPZ, ...this._mapMarkers].forEach(pz => {
        if (!pz) return;
        const idx = this.elements.indexOf(pz);
        if (idx !== -1) { pz.remove(); this.elements.splice(idx, 1); }
        if (this.selected === pz) {
          this.selected = null;
          if (this.handlesOverlay) this.handlesOverlay.hidden = true;
        }
      });
      this._mapTitlePZ   = null;
      this._mapCoordsPZ  = null;
      this._mapMarkers.clear();
      if (this.mapMarkerRow) this.mapMarkerRow.hidden = true;
      this._mapTabLoaded = false;
      this.mapState      = null;
      this._setPhotoTabDisabled(false);
      if (this._mapZoomFloat) this._mapZoomFloat.hidden = true;
      if (this.mapRemoveBtn) this.mapRemoveBtn.hidden = true;
      if (this.mapCoordsEl) this.mapCoordsEl.textContent = '—';
      if (this.mapTitleInput) this.mapTitleInput.value = '';
      if (this.mapGeocoderWrap) this.mapGeocoderWrap.innerHTML = '';
      if (this.mapNoConfig) this.mapNoConfig.hidden = true;
      if (this.mapCustomCoordsCheck) this.mapCustomCoordsCheck.checked = false;
      if (this.mapCustomCoordsInput) { this.mapCustomCoordsInput.hidden = true; this.mapCustomCoordsInput.value = ''; }
      this._updateAtcState();
      this._doSwitchTab('photo');
    }

    _applyDisabledTabs() {
      const tabs = this._disabledTabs || [];
      this.tabBtns.forEach(btn => {
        const name = btn.dataset.tab;
        const hide = tabs.includes(name);
        btn.hidden = hide;
        if (hide) btn.classList.remove('is-active');
      });
    }

    _setPhotoTabDisabled(disabled) {
      const photoTab = document.querySelector('.pz-tab[data-tab="photo"]');
      if (!photoTab) return;
      photoTab.classList.toggle('pz-tab--disabled', disabled);
      photoTab.setAttribute('aria-disabled', String(disabled));
      if (disabled) {
        photoTab.setAttribute('title', 'Verwijder eerst de stadskaart om een foto te uploaden.');
      } else {
        photoTab.removeAttribute('title');
      }
    }

    _setMapTabDisabled(disabled, reason) {
      const mapTab = document.querySelector('.pz-tab[data-tab="map"]');
      if (!mapTab) return;
      mapTab.classList.toggle('pz-tab--disabled', disabled);
      mapTab.setAttribute('aria-disabled', String(disabled));
      if (disabled) {
        mapTab.setAttribute('title', reason || 'Verwijder eerst de foto om een stadskaart te gebruiken.');
      } else {
        mapTab.removeAttribute('title');
      }
    }

    _updateMapTabState() {
      const currentDeg = (this._allowedRotations && this._bgRotIdx !== undefined)
        ? (this._allowedRotations[this._bgRotIdx] || 0) : 0;
      const disabledByRotation = ((this._disabledTabsByRotation || {}).map || []).includes(currentDeg);
      const hasPhoto = this.elements.some(e => e.type === 'photo');
      const willDisable = disabledByRotation || hasPhoto;
      if (disabledByRotation) {
        this._setMapTabDisabled(true, 'Stadskaart is niet beschikbaar bij deze rotatie.');
      } else {
        this._setMapTabDisabled(hasPhoto);
      }
      // If disabling while the map panel is currently active, switch away to the designer tab
      if (willDisable) {
        const activeTab = [...this.tabBtns].find(b => b.classList.contains('is-active'));
        if (activeTab && activeTab.dataset.tab === 'map') {
          this._doSwitchTab('text');
        }
      }
    }

    _updateTextTabWarning() {
      const textPanel = document.getElementById('tab-panel-text');
      if (!textPanel) return;
      const hasPhoto = this.elements.some(e => e.type === 'photo');
      let warning = textPanel.querySelector('.pz-text-photo-warning');
      if (hasPhoto) {
        if (!warning) {
          warning = document.createElement('div');
          warning.className = 'pz-text-photo-warning';
          warning.setAttribute('role', 'note');
          warning.style.cssText =
            'display:flex;align-items:flex-start;gap:8px;margin:0 0 14px;padding:10px 12px;' +
            'background:#fff8e1;border:1px solid #f9a825;border-radius:6px;' +
            'font-size:13px;line-height:1.45;color:#5d4037;position:relative';
          warning.innerHTML =
            '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="flex-shrink:0;margin-top:1px"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>' +
            '<span style="flex:1">Tekst over een foto is zeer slecht leesbaar, na het graveren. Zet tekst naast of onder de foto.</span>' +
            '<button type="button" aria-label="Melding sluiten" style="flex-shrink:0;background:none;border:none;cursor:pointer;padding:0;margin-top:-1px;color:#8a6a4a;line-height:1">' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
            '</button>';
          warning.querySelector('button').addEventListener('click', () => { warning.hidden = true; });
          textPanel.insertBefore(warning, textPanel.firstChild);
        }
        warning.hidden = false;
      } else if (warning) {
        warning.hidden = true;
      }
    }

    _bindZoomBtn(btn, delta) {
      btn.addEventListener('pointerdown', (e) => { e.preventDefault(); this._startMapZoom(delta); });
      btn.addEventListener('pointerup',     () => this._stopMapZoom());
      btn.addEventListener('pointerleave',  () => this._stopMapZoom());
      btn.addEventListener('pointercancel', () => this._stopMapZoom());
    }

    _startMapZoom(delta) {
      this._stopMapZoom();
      const step = () => {
        if (!this._mapInstance) return;
        this._mapInstance.easeTo({ zoom: this._mapInstance.getZoom() + delta, duration: 80 });
        this._mapZoomTimer = setTimeout(step, 80);
      };
      step();
    }

    _stopMapZoom() {
      clearTimeout(this._mapZoomTimer);
      this._mapZoomTimer = null;
    }

    _getMapBoundsInSafeZone() {
      if (!this._mapContainer || !this.safeZone) return null;
      const szR = this.safeZone.getBoundingClientRect();
      const mR  = this._mapContainer.getBoundingClientRect();
      const cx  = (mR.left + mR.right)   / 2 - szR.left;
      const cy  = (mR.top  + mR.bottom)  / 2 - szR.top;
      const r   = (mR.right - mR.left)   / 2;
      return {
        x1: mR.left  - szR.left, y1: mR.top    - szR.top,
        x2: mR.right - szR.left, y2: mR.bottom - szR.top,
        cx, cy, r,
        isRound: this._mapContainer.classList.contains('pz-map-zone--round')
      };
    }

    _addMapMarker(type) {
      if (!this._mapInstance) return;
      const svgContent = MAP_MARKER_SVGS[type];
      if (!svgContent) return;
      const bounds = this._getMapBoundsInSafeZone();
      if (!bounds) return;
      const el = new PZElement('svg', svgContent, { scale: 0.8 });
      el.mount(this.safeContent);
      el.el.classList.add('pz-map-marker-el');
      // Override default center to map center
      const szW = this.safeZone.offsetWidth;
      const szH = this.safeZone.offsetHeight;
      el.x     = bounds.cx;
      el.y     = bounds.cy;
      el.xFrac = szW ? bounds.cx / szW : 0.5;
      el.yFrac = szH ? bounds.cy / szH : 0.5;
      el._applyTransform();
      this.elements.push(el);
      this._mapMarkers.add(el);
      this.select(el);
      this._updateAtcState();
    }

    /* ---- Photo ---- */
    _processPhoto(file) {
      if (this._mapInstance) return; // map active — no photo
      if (this.elements.some(e => e.type === 'photo')) {
        this._showTabMessage('Je kunt maar 1 foto gebruiken. Verwijder de huidige foto eerst.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          const MAX   = 1200;
          const ratio = Math.min(MAX / img.width, MAX / img.height, 1);
          const w     = Math.round(img.width  * ratio);
          const h     = Math.round(img.height * ratio);

          // Origineel canvas — wordt opgeslagen op de server
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);

          // Jarvis-preview op volledige resolutie — fijnere stippen bij grote weergave
          const prev = document.createElement('canvas');
          prev.width = w; prev.height = h;
          prev.getContext('2d').drawImage(img, 0, 0, w, h);
          _applyJarvis(prev);

          const { w: DW, h: DH } = this._designSize();
          const initialScale = Math.min(DW / w, DH / h);
          this._addElement('photo', canvas.toDataURL('image/png'), {
            scale:    initialScale,
            preview:  prev.toDataURL('image/png'),
            naturalW: w,
            naturalH: h,
          });
          this._showPhotoInfo();
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    }

    /* ---- Text ---- */
    _addText() {
      const text = this.textInput ? this.textInput.value.trim() : '';
      if (!text) {
        if (this.textInput) this.textInput.focus();
        return;
      }
      const font        = this.fontSelect    ? this.fontSelect.value    : "'Playfair Display', serif";
      const displaySize = this.fontSizeRange ? +this.fontSizeRange.value : 28;
      const displayH    = this.safeZone && this.safeZone.offsetHeight ? this.safeZone.offsetHeight : 600;

      // Store font size as a fraction of safe zone height — screen-independent.
      const fontSizeFrac = displayH > 0 ? displaySize / displayH : 0.05;

      this._addElement('text', text, { fontFamily: font, fontSizeFrac });
      if (this.textInput)   this.textInput.value = '';
      if (this.fontPreview) this.fontPreview.textContent = 'Voorbeeld tekst';
    }

    /* ---- SVG ---- */
    _addSVG(btn) {
      const svgEl = btn.querySelector('svg');
      if (!svgEl) return;
      const clone = svgEl.cloneNode(true);
      clone.removeAttribute('aria-hidden');
      clone.setAttribute('aria-label', btn.getAttribute('aria-label') || 'Symbool');
      // Initial scale: SVG base is 100px in design canvas; start at 25% of canvas width.
      const { w: DW } = this._designSize();
      const initialScale = DW * 0.25 / 100;
      this._addElement('svg', clone.outerHTML, { scale: initialScale });
    }

    /* ---- Generic add ---- */
    _addElement(type, content, opts) {
      if (!this.safeContent) return; // safe zone not yet initialised
      const el = new PZElement(type, content, opts);
      el.mount(this.safeContent);
      // If a map is active, push the element below the map so it doesn't land on top of it
      if (this._mapInstance && this.safeZone) {
        const szH = this.safeZone.offsetHeight || 400;
        const szW = this.safeZone.offsetWidth  || 300;
        const _szPct      = this._mapSizePct || 90;
        const _offs       = _getMapOffsets(this._mapPosition, _szPct);
        const mapBottomPx = szH * (_offs.top / 100) + szW * (_szPct / 100);
        const targetY = Math.min(mapBottomPx + szH * 0.08, szH * 0.93);
        el.y     = targetY;
        el.yFrac = szH > 0 ? targetY / szH : 0.88;
        el._applyTransform();
      }
      this.elements.push(el);
      if (type === 'photo') { this._updateMapTabState(); this._updateTextTabWarning(); }
      this.select(el);
      this._updateAtcState();
      // On mobile: collapse options so canvas takes more screen
      if (this._isMobile()) this._collapsePanel();
    }

    /* ---- Selection ---- */
    select(el) {
      this.deselectAll();
      this.selected = el;
      el.select();
      this._updateControlPanel();
      this._updateMobileBar();
      this._updateHandlesOverlay();
    }

    deselectAll() {
      this.elements.forEach(e => e.deselect());
      this.selected = null;
      if (this.handlesOverlay) this.handlesOverlay.hidden = true;
      this._updateControlPanel();
      this._updateMobileBar();
    }

    removeElement(id) {
      const idx = this.elements.findIndex(e => e.id === id);
      if (idx === -1) return;
      const el = this.elements[idx];
      if (this.selected === el) {
        this.selected = null;
        if (this.handlesOverlay) this.handlesOverlay.hidden = true;
        this._updateControlPanel();
        this._updateMobileBar();
      }
      el.remove();
      this.elements.splice(idx, 1);
      this._mapMarkers.delete(el);
      // Update map tab state when last photo is removed (and no map active)
      if (el.type === 'photo' && !this.elements.some(e => e.type === 'photo') && !this._mapInstance) {
        this._updateMapTabState();
        this._updateTextTabWarning();
      }
      this._updateAtcState();
    }

    _updateControlPanel() {
      if (!this.elControls) return;
      if (this.selected) {
        this.elControls.hidden = false;
        const scaleMax = this.selected.type === 'svg' ? 20 : 6;
        if (this.ctrlScale) { this.ctrlScale.max = scaleMax; this.ctrlScale.value = this.selected.scale; }
        if (this.ctrlRotation) {
          this.ctrlRotation.value = this.selected.rotation;
          if (this.ctrlRotVal) this.ctrlRotVal.textContent = Math.round(this.selected.rotation) + '°';
        }
      } else {
        this.elControls.hidden = true;
      }
    }

    /* ---- Pointer interaction (drag / rotate / scale) ---- */
    startInteraction(action, el, e) {
      const sz   = this.safeZone;
      const szR  = sz.getBoundingClientRect();
      const elCX = szR.left + el.x;
      const elCY = szR.top  + el.y;

      this.ix = {
        action,
        el,
        startEl:    { x: el.x, y: el.y, scale: el.scale, rotation: el.rotation },
        startPtr:   { x: e.clientX, y: e.clientY },
        startAngle: Math.atan2(e.clientY - elCY, e.clientX - elCX),
        startDist:  Math.hypot(e.clientX - elCX, e.clientY - elCY) || 1
      };
      document.body.style.userSelect = 'none';
      try { e.target.setPointerCapture(e.pointerId); } catch (_) {}
    }

    _onPointerMove(e) {
      if (!this.ix) return;
      const { action, el, startEl, startPtr, startAngle, startDist } = this.ix;
      const sz  = this.safeZone;
      const szR = sz.getBoundingClientRect();

      if (action === 'drag') {
        let nx = startEl.x + (e.clientX - startPtr.x);
        let ny = startEl.y + (e.clientY - startPtr.y);
        let snapH = false, snapV = false;
        if (this._mapMarkers.has(el)) {
          // Constrain map marker inside map bounds
          const mb = this._getMapBoundsInSafeZone();
          if (mb) {
            if (mb.isRound) {
              const dx = nx - mb.cx, dy = ny - mb.cy;
              const d  = Math.hypot(dx, dy);
              if (d > mb.r) { nx = mb.cx + dx / d * mb.r; ny = mb.cy + dy / d * mb.r; }
            } else {
              nx = Math.max(mb.x1, Math.min(mb.x2, nx));
              ny = Math.max(mb.y1, Math.min(mb.y2, ny));
            }
          }
        } else {
          // Clamp to safe zone bounds so element centre never escapes.
          // For text: pivot (el.y) is at the ink midline; actual ink top/bottom extend
          // by ±displayFs/2, so clamp pivot to keep ink inside the frame.
          nx = Math.max(0, Math.min(sz.offsetWidth, nx));
          const inkHalfH = (el.type === 'text' && el._inkHalfH) ? el._inkHalfH : 0;
          ny = Math.max(inkHalfH, Math.min(sz.offsetHeight - inkHalfH, ny));
          // Snap to zone centre
          const SNAP  = 12;
          const cx    = sz.offsetWidth  / 2;
          const cy    = sz.offsetHeight / 2;
          snapH = Math.abs(nx - cx) < SNAP;
          snapV = Math.abs(ny - cy) < SNAP;
          if (snapH) nx = cx;
          if (snapV) ny = cy;
        }
        el.x = nx; el.y = ny;
        el.xFrac = sz.offsetWidth  ? nx / sz.offsetWidth  : el.xFrac;
        el.yFrac = sz.offsetHeight ? ny / sz.offsetHeight : el.yFrac;
        el._applyTransform();
        // snapH = X is at centre → vertical guide; snapV = Y at centre → horizontal guide
        this._showGuides(snapV, snapH);
      }

      if (action === 'rotate') {
        const cx    = szR.left + el.x;
        const cy    = szR.top  + el.y;
        const angle = Math.atan2(e.clientY - cy, e.clientX - cx);
        let r = startEl.rotation + (angle - startAngle) * (180 / Math.PI);
        r = _snapAngle(r); // snap to 0°, ±45°, ±90°, ±135°, ±180°
        el.rotation = r;
        el._applyTransform();
        if (this.ctrlRotation && this.selected === el) {
          this.ctrlRotation.value = Math.round(r);
          if (this.ctrlRotVal) this.ctrlRotVal.textContent = Math.round(r) + '°';
        }
      }

      if (action === 'scale') {
        const cx   = szR.left + el.x;
        const cy   = szR.top  + el.y;
        const dist = Math.hypot(e.clientX - cx, e.clientY - cy) || 1;
        const scaleMax = el.type === 'svg' ? 20 : 6;
        el.scale   = Math.max(0.1, Math.min(scaleMax, startEl.scale * (dist / startDist)));
        el._applyTransform();
        if (this.ctrlScale && this.selected === el) {
          this.ctrlScale.value = el.scale;
        }
      }
    }

    _onPointerUp() {
      this.ix = null;
      document.body.style.userSelect = '';
      this._showGuides(false, false);
      if (this.selected) {
        this._updateHandlesOverlay();
      }
    }

    /* ---- Capture Mapbox canvas reliably (triggerRepaint → render event → toDataURL) ---- */
    _captureMapCanvas() {
      if (!this._mapInstance) return Promise.resolve(null);
      return new Promise(resolve => {
        const map   = this._mapInstance;
        const hasBg = map.getLayer('background');
        // Tijdelijk achtergrond herstellen zodat GL canvas niet leeg/zwart is bij capture
        if (hasBg) map.setPaintProperty('background', 'background-opacity', 1);
        const capture = () => {
          try {
            const dataUrl = map.getCanvas().toDataURL('image/png');
            if (hasBg) map.setPaintProperty('background', 'background-opacity', 0);
            resolve(dataUrl);
          } catch (_) {
            if (hasBg) map.setPaintProperty('background', 'background-opacity', 0);
            resolve(null);
          }
        };
        const timer = setTimeout(capture, 600);
        map.once('render', () => { clearTimeout(timer); capture(); });
        map.triggerRepaint();
      });
    }

    /* ---- Preview PNG ---- */
    async _generatePreview() {
      if (!this.safeZone) return null;

      const { w: DW, h: DH } = this._designSize();

      // Pre-load all text fonts so fontBoundingBoxAscent uses actual font metrics,
      // not the fallback font (which would cause a baseline mismatch with the SVG).
      await Promise.all(
        this.elements
          .filter(el => el.type === 'text')
          .map(el => {
            const fs = Math.max(1, el.fontSizeFrac * DH);
            return document.fonts.load(fs + 'px ' + el.fontFamily).catch(() => {});
          })
      );

      const canvas  = document.createElement('canvas');
      canvas.width  = DW;
      canvas.height = DH;
      const ctx     = canvas.getContext('2d');

      // Draw map first (background layer)
      if (this._mapInstance && this._mapContainer) {
        const mapDataUrl = await this._captureMapCanvas();
        if (mapDataUrl) {
          await new Promise(resolve => {
            const img = new Image();
            img.onload = () => {
              const _mszPct  = this._mapSizePct || 90;
              const _moffs   = _getMapOffsets(this._mapPosition, _mszPct);
              const mapW = Math.round(DW * _mszPct  / 100);
              const mapX = Math.round(DW * _moffs.left / 100);
              const mapY = Math.round(DH * _moffs.top  / 100);
              ctx.save();
              if (this._mapContainer.classList.contains('pz-map-zone--round')) {
                ctx.beginPath();
                ctx.arc(mapX + mapW / 2, mapY + mapW / 2, mapW / 2, 0, Math.PI * 2);
                ctx.clip();
              }
              ctx.drawImage(img, mapX, mapY, mapW, mapW);
              ctx.restore();
              resolve();
            };
            img.onerror = resolve;
            img.src     = mapDataUrl;
          });
        }
      }

      for (const el of this.elements) {
        ctx.save();
        // Use screen-independent fractions × fixed design dimensions
        ctx.translate(el.xFrac * DW, el.yFrac * DH);
        ctx.rotate(el.rotation * Math.PI / 180);
        ctx.scale(el.scale, el.scale);

        if (el.type === 'photo') {
          await new Promise(resolve => {
            const img = new Image();
            img.onload = () => {
              ctx.drawImage(img, -img.width / 2, -img.height / 2);
              resolve();
            };
            img.onerror = resolve;
            // Gebruik de Jarvis-dithered B&W preview zodat de cart-thumbnail ook zwart-wit is
            img.src = el.preview || el.content;
          });

        } else if (el.type === 'text') {
          const fs = Math.max(1, el.fontSizeFrac * DH);
          ctx.font      = fs + 'px ' + el.fontFamily;
          ctx.fillStyle = '#000';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'alphabetic';
          const m       = ctx.measureText(el.content);
          // Gebruik actualBoundingBoxAscent (precieze inktgrens voor déze tekst) zodat
          // de preview en de Python-SVG op exact dezelfde baseline-positie tekenen.
          const ascent  = m.actualBoundingBoxAscent  ?? m.fontBoundingBoxAscent  ?? (fs * 0.8);
          el._fontBBoxAscentFrac = ascent / fs;
          const baseY   = ascent - fs / 2;
          ctx.fillText(el.content, 0, baseY);

        } else if (el.type === 'svg') {
          const sw = el.svgW || 100, sh = el.svgH || 100;
          // Strip any existing width/height from the root <svg> tag before adding ours.
          // Simply prepending creates duplicate XML attributes which cause a fatal parse error,
          // so img.onerror fires and the symbol is silently skipped.
          const sized = el.content
            .replace(/(<svg\b[^>]*?)\s+width="[^"]*"([^>]*?>)/i,  '$1$2')
            .replace(/(<svg\b[^>]*?)\s+height="[^"]*"([^>]*?>)/i, '$1$2')
            .replace(/<svg\b/i, '<svg width="' + sw + '" height="' + sh + '"');
          await new Promise(resolve => {
            const blob = new Blob([sized], { type: 'image/svg+xml' });
            const url  = URL.createObjectURL(blob);
            const img  = new Image();
            img.onload = () => {
              ctx.drawImage(img, -sw / 2, -sh / 2, sw, sh);
              URL.revokeObjectURL(url);
              resolve();
            };
            img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
            img.src = url;
          });
        }

        ctx.restore();
      }

      return canvas.toDataURL('image/png');
    }

    /* ---- Wrapper preview: productafbeelding + design overlay (JPEG) ----
       Haalt de productafbeelding op via fetch() als blob zodat er geen
       cross-origin canvas-taint ontstaat — geen toestemmingsvenster nodig,
       werkt op iOS Safari, Android en desktop.
    ---- */
    async _generateWrapperPreview() {
      const img = this.productImage;
      if (!img || !img.naturalWidth) return null;

      // Fetch product image als blob (Shopify CDN stuurt Access-Control-Allow-Origin: *)
      // Query-suffix voorkomt dat een zonder-CORS gecachte versie wordt gebruikt.
      // Als img.src al een data: URL is (gedraaid product via canvas), direct laden — geen fetch nodig.
      let productEl;
      try {
        if (img.src.startsWith('data:')) {
          productEl = await new Promise((resolve, reject) => {
            const i = new Image();
            i.onload  = () => resolve(i);
            i.onerror = () => reject(new Error('img load'));
            i.src = img.src;
          });
        } else {
          const sep    = img.src.includes('?') ? '&' : '?';
          const res    = await fetch(img.src + sep + '_pz=1', { mode: 'cors', cache: 'no-store' });
          if (!res.ok) throw new Error(res.status);
          const blob   = await res.blob();
          const blobUrl = URL.createObjectURL(blob);
          productEl = await new Promise((resolve, reject) => {
            const i = new Image();
            i.onload  = () => { URL.revokeObjectURL(blobUrl); resolve(i); };
            i.onerror = () => { URL.revokeObjectURL(blobUrl); reject(new Error('img load')); };
            i.src = blobUrl;
          });
        }
      } catch (e) {
        console.warn('[Personalizer] Wrapper preview: product image fetch mislukt', e);
        return null;
      }

      // Design canvas (alleen safe zone-inhoud) via de bestaande methode
      const designDataUrl = await this._generatePreview();
      if (!designDataUrl) return null;

      const designImg = await new Promise(resolve => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.src    = designDataUrl;
      });

      // Output-canvas: maximaal 1400px breed om payload beheersbaar te houden
      const natW = productEl.naturalWidth;
      const natH = productEl.naturalHeight;
      const MAX_W = 1400;
      const outW  = Math.min(natW, MAX_W);
      const outH  = Math.round(natH * outW / natW);

      const canvas = document.createElement('canvas');
      canvas.width  = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');

      // Witte achtergrond zodat transparante pixels in de productafbeelding niet zwart worden in JPEG
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, outW, outH);

      // Productafbeelding als achtergrond
      ctx.drawImage(productEl, 0, 0, outW, outH);

      // Safe zone positie in output-canvas coördinaten.
      // _imgContentRect geeft de content-rechthoek van de <img> in display-pixels
      // (corrigeert voor object-fit letterboxing). Daarna omrekenen naar output-pixels.
      const dispRect = _imgContentRect(img);
      if (dispRect.width && dispRect.height) {
        const scX = outW / dispRect.width;
        const scY = outH / dispRect.height;
        const cfg = this.safeZoneCfg;
        const szL = cfg.x      / 100 * dispRect.width  * scX;
        const szT = cfg.y      / 100 * dispRect.height * scY;
        const szW = cfg.width  / 100 * dispRect.width  * scX;
        const szH = cfg.height / 100 * dispRect.height * scY;
        ctx.drawImage(designImg, szL, szT, szW, szH);
      }

      try {
        return canvas.toDataURL('image/jpeg', 0.88);
      } catch (e) {
        console.warn('[Personalizer] Wrapper preview: toDataURL mislukt', e);
        return null;
      }
    }

    /* ---- Design canvas size (screen-independent) ---- */
    _designSize() {
      const W      = 1000;
      const szCfg  = this.safeZoneCfg;
      const img    = this.productImage;
      // Use product image natural dimensions to derive the physical aspect ratio
      // of the safe zone — this never changes regardless of screen size
      const H = (img && img.naturalWidth && img.naturalHeight)
        ? Math.round(W * szCfg.height * img.naturalHeight / (szCfg.width * img.naturalWidth))
        : Math.round(W * szCfg.height / szCfg.width);
      return { w: W, h: H };
    }

    /* ---- Opslaan + ATC ---- */
    async _saveDesignAndAddToCart() {
      if (!this.confirmCheck || !this.confirmCheck.checked) return;
      if (this._uitgesnedenMode) { this._saveUitgesnedenAndAddToCart(); return; }
      if (this.elements.length === 0 && !this._mapInstance) return;
      if (!this.variantId) {
        console.warn('[Personalizer] Geen variant ID gevonden.');
        return;
      }

      // Validate SVG elements — reject if any contain XML parse errors
      const badSvg = this.elements.find(el => el.type === 'svg' && !_isSvgValid(el.content));
      if (badSvg) {
        alert('Eén of meer symbolen bevatten een ongeldige afbeelding en kunnen niet worden verwerkt. Verwijder het symbool en probeer opnieuw.');
        return;
      }

      const pCfg   = window.personalizerConfig || {};
      const apiUrl = this._dynApiUrl || pCfg.apiUrl;
      if (!apiUrl) {
        console.warn('[Personalizer] Geen apiUrl — valt terug op _addToCart.');
        this._addToCart();
        return;
      }

      const btn                = this.confirmBtn || this.atcBtn;
      const origText           = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Preview maken…'; }

      // Wrapper preview: productafbeelding + design overlay — zonder browser-toestemming
      const wrapperPreview = await this._generateWrapperPreview();
      if (btn) btn.textContent = 'Ontwerp opslaan…';

      // Vaste ontwerpresolutie gebaseerd op product-afbeelding — schermgrootte-onafhankelijk
      const { w: DW, h: DH } = this._designSize();

      // Zorg dat fontBBoxAscent voor alle tekstelementen is gemeten vóór opslaan.
      // _generatePreview() doet dit ook, maar als de preview overgeslagen werd
      // (b.v. bij herlaad van een bestaand ontwerp) is _fontBBoxAscentFrac undefined.
      for (const el of this.elements) {
        if (el.type === 'text' && !el._fontBBoxAscentFrac) {
          try {
            const fs = Math.max(1, el.fontSizeFrac * DH);
            await document.fonts.load(fs + 'px ' + el.fontFamily).catch(() => {});
            const _mc = document.createElement('canvas').getContext('2d');
            _mc.font  = fs + 'px ' + el.fontFamily;
            const _m  = _mc.measureText(el.content);
            const _asc = _m.actualBoundingBoxAscent || _m.fontBoundingBoxAscent;
            if (_asc) el._fontBBoxAscentFrac = _asc / fs;
          } catch (_e) { /* Python valt terug op sTypoAscender */ }
        }
      }

      // Bijgesneden kleurenversie per foto-element: foto getekend op DW×DH canvas
      // op de juiste positie/schaal/rotatie — toont exact wat zichtbaar is in de safe zone.
      for (const el of this.elements) {
        if (el.type !== 'photo') continue;
        const cropCanvas = document.createElement('canvas');
        cropCanvas.width  = DW;
        cropCanvas.height = DH;
        const cropCtx = cropCanvas.getContext('2d');
        await new Promise(resolve => {
          const img = new Image();
          img.onload = () => {
            cropCtx.save();
            cropCtx.translate(el.xFrac * DW, el.yFrac * DH);
            cropCtx.rotate(el.rotation * Math.PI / 180);
            cropCtx.scale(el.scale, el.scale);
            cropCtx.drawImage(img, -img.width / 2, -img.height / 2);
            cropCtx.restore();
            el._contentCropped = cropCanvas.toDataURL('image/jpeg', 0.92);
            resolve();
          };
          img.onerror = resolve;
          img.src = el.content;
        });
      }

      const payload = {
        product_id:    this._dynProductId    || pCfg.productId,
        variant_id:    this.variantId,
        product_title: this._dynProductTitle || pCfg.productTitle,
        safe_zone:     this.safeZoneCfg,
        safe_zone_px:  { width: DW, height: DH },
        elements: this.elements.map(el => {
          const s = el.serialize();
          const out = { ...s, x: Math.round(el.xFrac * DW), y: Math.round(el.yFrac * DH), fontBBoxAscent: el._fontBBoxAscentFrac ?? 0 };
          if (el.type === 'photo' && el._contentCropped) out.content_cropped = el._contentCropped;
          return out;
        }),
      };

      if (this._mapInstance && this.mapState) {
        const c = this._mapInstance.getCenter();
        // canvas_width: GL canvas geeft soms 0 terug als container niet zichtbaar is.
        // Gebruik mapContainer.clientWidth × dpr als fallback.
        const _dpr = window.devicePixelRatio || 1;
        const _glW  = this._mapInstance.getCanvas().width;
        const _ctrW = this._mapContainer ? Math.round(this._mapContainer.clientWidth * _dpr) : 0;
        payload.map = {
          center:       { lng: c.lng, lat: c.lat },
          zoom:         this._mapInstance.getZoom(),
          dpr:          _dpr,
          canvas_width: _glW || _ctrW,
          title:        this.mapTitleInput ? this.mapTitleInput.value : (this.mapState.title || ''),
          coords:       this.mapCoordsEl ? this.mapCoordsEl.textContent : '',
          shape:        (this.mapState && this.mapState.shape) || 'square',
        };
      }

      // Meesturen als JPEG data URL — API slaat dit op als preview-afbeelding van de plank
      if (wrapperPreview) payload.wrapper_preview = wrapperPreview;

      let designId;
      try {
        const res = await fetch(apiUrl + 'public/designs', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('API ' + res.status);
        const data = await res.json();
        designId   = data.design_id;
      } catch (err) {
        console.error('[Personalizer] Design opslaan mislukt:', err);
        if (btn) { btn.disabled = false; btn.textContent = origText; }
        alert('Er ging iets mis bij het opslaan van je ontwerp. Probeer het opnieuw.');
        return;
      }

      if (btn) btn.textContent = 'Toevoegen…';

      try {
        const r = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id:       this.variantId,
            quantity: this.qty || 1,
            properties: {
              '_pz_design_id':   designId,
              '_pz_preview_url': apiUrl + 'public/designs/' + designId + '/wrapper-preview',
              'Personalisatie':  'Ja - ontwerp bevestigd door klant'
            }
          })
        });
        if (!r.ok) throw new Error('Cart ' + r.status);
        this._reset();
        this.close();
        window.location.href = this.cartUrl;
      } catch (err) {
        console.error('[Personalizer] Winkelwagen toevoegen mislukt:', err);
        if (btn) { btn.disabled = false; btn.textContent = origText; }
        alert('Er ging iets mis bij het toevoegen aan de winkelwagen. Probeer het opnieuw.');
      }
    }

    /* ---- Uitsneden kaart ---- */
    async _saveUitgesnedenAndAddToCart() {
      if (!this.confirmCheck || !this.confirmCheck.checked) return;
      if (!this.variantId) { console.warn('[Personalizer] Geen variant ID.'); return; }

      const _textCheck = document.getElementById('pz-uk-text');
      if (!_textCheck || !_textCheck.value.trim()) {
        if (_textCheck) { _textCheck.focus(); _textCheck.reportValidity(); }
        return;
      }

      const btn      = this.confirmBtn || this.atcBtn;
      const origText = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Toevoegen…'; }

      const map      = this._ukMap;
      const center   = map ? map.getCenter() : { lat: 0, lng: 0 };
      const zoom     = map ? map.getZoom()   : 13;
      const textEl   = document.getElementById('pz-uk-text');
      const barText  = textEl ? textEl.value.trim() : '';

      // Proportionele layout uit DOM — server reproduceert exact dezelfde indeling
      const mapEl     = document.getElementById('pz-uk-map');
      const previewEl = document.getElementById('pz-uk-preview');
      const mapHeightPct = (mapEl && previewEl && previewEl.offsetHeight)
        ? +(mapEl.offsetHeight / previewEl.offsetHeight * 100).toFixed(2)
        : 85;

      const pCfg   = window.personalizerConfig || {};
      const apiUrl = this._dynApiUrl || pCfg.apiUrl;

      // Sla ontwerp op in de database (zichtbaar in dashboard onder custom bestellingen)
      let designId;
      if (apiUrl) {
        try {
          // Fysieke afmetingen in px bij 300 DPI (40×48.5 cm)
          const UK_W_PX = Math.round(40   / 2.54 * 300); // 4724
          const UK_H_PX = Math.round(48.5 / 2.54 * 300); // 5728
          const payload = {
            type:          'uitsneden',
            product_id:    this._dynProductId    || pCfg.productId,
            variant_id:    this.variantId,
            product_title: this._dynProductTitle || pCfg.productTitle,
            safe_zone_px:  { width: UK_W_PX, height: UK_H_PX },
            elements:      [],
            uitsneden: {
              center:         { lat: +center.lat.toFixed(6), lng: +center.lng.toFixed(6) },
              zoom:           +zoom.toFixed(4),
              title:          barText,
              style_url:      this._uitgesnedenStyleUrl || 'mapbox://styles/mapbox/streets-v12',
              dpr:            window.devicePixelRatio || 1,
              canvas_width:   map ? map.getCanvas().width  : 0,
              canvas_height:  map ? map.getCanvas().height : 0,
              map_height_pct: mapHeightPct,
              dimensions_cm:  { width: 40, height: 48.5 },
            },
          };
          const res = await fetch(apiUrl + 'public/designs', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify(payload)
          });
          if (!res.ok) throw new Error('API ' + res.status);
          const data = await res.json();
          designId   = data.design_id;
        } catch (err) {
          console.error('[Personalizer] Uitsneden design opslaan mislukt:', err);
          if (btn) { btn.disabled = false; btn.textContent = origText; }
          alert('Er ging iets mis bij het opslaan van je ontwerp. Probeer het opnieuw.');
          return;
        }
      }

      try {
        const properties = {
          '_uk_lat':        String(center.lat.toFixed(6)),
          '_uk_lng':        String(center.lng.toFixed(6)),
          '_uk_zoom':       String(zoom.toFixed(2)),
          '_uk_tekst':      barText,
          'Personalisatie': 'Uitsneden stadskaart' + (barText ? ' - ' + barText : '') + ' - ontwerp bevestigd door klant',
        };
        if (designId) {
          properties['_pz_design_id']   = designId;
          properties['_pz_preview_url'] = apiUrl + 'public/designs/' + designId + '/wrapper-preview';
        }

        const r = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id:       this.variantId,
            quantity: this.qty || 1,
            properties,
          })
        });
        if (!r.ok) throw new Error('Cart ' + r.status);
        this._reset();
        this.close();
        window.location.href = this.cartUrl;
      } catch (err) {
        console.error('[Personalizer] Uitsneden toevoegen mislukt:', err);
        if (btn) { btn.disabled = false; btn.textContent = origText; }
        alert('Er ging iets mis bij het toevoegen aan de winkelwagen. Probeer het opnieuw.');
      }
    }

    _initUitgesnedenMap(token, styleUrl) {
      var self          = this;
      var MAPBOX_GL_CSS = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css';
      var MAPBOX_GL_JS  = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js';
      var GEOCODER_CSS  = 'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-geocoder/v5.0.0/mapbox-gl-geocoder.css';
      var GEOCODER_JS   = 'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-geocoder/v5.0.0/mapbox-gl-geocoder.min.js';

      function _ldCss(href) {
        if (document.querySelector('link[href="' + href + '"]')) return;
        var el = document.createElement('link'); el.rel = 'stylesheet'; el.href = href;
        document.head.appendChild(el);
      }
      function _ldJs(src, cb) {
        if (document.querySelector('script[src="' + src + '"]')) { cb(); return; }
        var el = document.createElement('script'); el.src = src; el.onload = cb;
        document.head.appendChild(el);
      }

      _ldCss(MAPBOX_GL_CSS);
      _ldCss(GEOCODER_CSS);
      _ldJs(MAPBOX_GL_JS, function () {
        _ldJs(GEOCODER_JS, function () {
          var mapEl     = document.getElementById('pz-uk-map');
          var geocEl    = document.getElementById('pz-uk-geocoder');
          var textInput = document.getElementById('pz-uk-text');
          var barText   = document.getElementById('pz-uk-bar-text');
          var btnIn     = document.getElementById('pz-uk-zoom-in');
          var btnOut    = document.getElementById('pz-uk-zoom-out');

          if (!mapEl || !token) {
            console.warn('[Uitsneden] Map niet geïnitialiseerd — mapEl:', !!mapEl, 'token:', token ? token.slice(0,8)+'…' : '(leeg)');
            return;
          }

          // Als de kaart al bestaat: resize nadat frame zichtbaar is
          if (self._ukMap) {
            requestAnimationFrame(function () { self._ukMap.resize(); });
            return;
          }

          console.log('[Uitsneden] Init Mapbox — container:', mapEl.clientWidth + '×' + mapEl.clientHeight, 'token:', token.slice(0,8)+'…');

          mapboxgl.accessToken = token;
          var map = new mapboxgl.Map({
            container:        mapEl,
            style:            styleUrl || 'mapbox://styles/mapbox/streets-v12',
            center:           [5.2913, 52.1326],
            minZoom:          9,
            zoom:             13,
            interactive:      true,
            attributionControl: false,
          });
          map.scrollZoom.enable();
          map.once('style.load', function () {
            map.resize();
            if (map.getLayer('background')) {
              map.setPaintProperty('background', 'background-opacity', 0);
            }
          });
          map.on('zoom', function () {
            console.log('[Uitsneden] zoom:', map.getZoom().toFixed(2));
          });
          map.on('click', function (e) {
            console.log('[Uitsneden] click:', e.lngLat.lat.toFixed(6) + ',' + e.lngLat.lng.toFixed(6));
          });

          var _lastSafeZoom = 13;
          var _snapping = false;
          map.on('zoomend', function () {
            var z = map.getZoom();
            if (_snapping) { _snapping = false; _lastSafeZoom = z; return; }
            if (z >= 11 && z < 12) {
              _snapping = true;
              if (_lastSafeZoom >= 12) {
                map.jumpTo({ zoom: 10.9 });
              } else {
                map.jumpTo({ zoom: 12 });
              }
            } else {
              _lastSafeZoom = z;
            }
          });

          self._ukMap = map;

          var geocoder = new MapboxGeocoder({
            accessToken: token,
            mapboxgl:    mapboxgl,
            marker:      false,
            placeholder: 'Zoek een stad of wijk…',
            language:    'nl',
            countries:   'nl,be',
            flyTo:       { speed: 1.2, essential: true },
          });
          if (geocEl) {
            geocEl.innerHTML = '';
            geocEl.appendChild(geocoder.onAdd(map));
          }

          function _fitUkBarText() {
            if (!barText || !barText.parentElement) return;
            barText.style.fontSize = '';
            var bar   = barText.parentElement;
            var maxW  = bar.offsetWidth * 0.88;
            if (barText.scrollWidth <= maxW) return;
            var fs = parseFloat(window.getComputedStyle(barText).fontSize);
            while (barText.scrollWidth > maxW && fs > 11) {
              fs -= 0.5;
              barText.style.fontSize = fs + 'px';
            }
          }

          geocoder.on('result', function (e) {
            var name = e.result.text || e.result.place_name.split(',')[0].trim();
            if (!textInput || textInput.dataset.manual) return;
            textInput.value = name;
            if (barText) { barText.textContent = name || 'Jouw stad'; _fitUkBarText(); }
          });

          if (textInput) {
            textInput.addEventListener('input', function () {
              textInput.dataset.manual = 'true';
              if (barText) { barText.textContent = textInput.value || 'Jouw stad'; _fitUkBarText(); }
            });
            textInput.addEventListener('change', function () {
              if (!textInput.value) delete textInput.dataset.manual;
            });
          }

          if (btnIn)  btnIn.addEventListener('click',  function () { map.zoomIn({ duration: 300 }); });
          if (btnOut) btnOut.addEventListener('click', function () { map.zoomOut({ duration: 300 }); });
        });
      });
    }

    /* ---- ATC ---- */
    _updateAtcState() {
      const hasElements = this.elements.length > 0 || !!this._mapInstance || !!this._uitgesnedenReady;

      // Stap 1: primaire knop — ingeschakeld zodra er elementen zijn
      if (this.atcBtn) {
        this.atcBtn.disabled = !hasElements;
        this.atcBtn.setAttribute('aria-disabled', String(!hasElements));
      }

      // Stap 2: bevestigen-knop — ingeschakeld als er elementen zijn én checkbox aan
      const ok = hasElements && !!this.confirmCheck && this.confirmCheck.checked;
      if (this.confirmBtn) {
        this.confirmBtn.disabled = !ok;
        this.confirmBtn.setAttribute('aria-disabled', String(!ok));
      }

      // Drive cart trigger state + icon colour
      if (this.cartTrigger) {
        this.cartTrigger.disabled = !hasElements;
        this.cartTrigger.classList.toggle('has-items', hasElements);
      }

      // Sluit drawer wanneer canvas leeg is
      if (!hasElements && this.atcDrawer) {
        this.atcDrawer.classList.remove('is-open');
        if (this.atcBtn) this.atcBtn.setAttribute('aria-expanded', 'false');
        if (this.atcBtnLabel) this.atcBtnLabel.textContent = 'In winkelwagen';
        const atcIcon2 = this.atcBtn ? this.atcBtn.querySelector('svg') : null;
        if (atcIcon2) atcIcon2.style.display = '';
      }
    }

    _addToCart() {
      if (!this.confirmCheck || !this.confirmCheck.checked) return;
      if (this.elements.length === 0) return;
      if (!this.variantId) {
        console.warn('[Personalizer] Geen variant ID gevonden.');
        return;
      }

      const design    = this.elements.map(el => el.serialize());
      const designStr = JSON.stringify(design);
      const btn2      = this.confirmBtn || this.atcBtn;
      const origText  = btn2 ? btn2.textContent : '';
      if (btn2) { btn2.disabled = true; btn2.textContent = 'Toevoegen…'; }

      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id:       this.variantId,
          quantity: this.qty || 1,
          properties: {
            '_pz_design':    designStr,
            'Personalisatie': 'Ja - ontwerp bevestigd door klant'
          }
        })
      })
      .then(r => { if (!r.ok) throw r; return r.json(); })
      .then(() => {
        this.close();
        window.location.href = this.cartUrl;
      })
      .catch(() => {
        if (btn2) { btn2.disabled = false; btn2.textContent = origText; }
        alert('Er ging iets mis bij het toevoegen aan de winkelwagen. Probeer het opnieuw.');
      });
    }
  }

  /* --------------------------------------------------
     Helpers
  -------------------------------------------------- */

  /* Snap angle to 0°, ±45°, ±90°, ±135°, ±180° within 8° threshold */
  function _snapAngle(angle) {
    const SNAPS  = [-180, -135, -90, -45, 0, 45, 90, 135, 180];
    const THRESH = 8;
    for (const s of SNAPS) {
      if (Math.abs(angle - s) < THRESH) return s;
    }
    return angle;
  }

  /* --------------------------------------------------
     Jarvis–Judice–Ninke dithering — preview in designer
  -------------------------------------------------- */
  function _applyJarvis(canvas) {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const w = canvas.width, h = canvas.height;
    const n  = w * h;
    const id  = ctx.getImageData(0, 0, w, h);
    const d   = id.data;

    // Stap 1: Perceptuele grijswaarden (alpha compositing: transparant → wit)
    const gray = new Float32Array(n);
    for (let p = 0; p < n; p++) {
      const a = d[p*4+3] / 255;
      gray[p] = (0.2126 * d[p*4] + 0.7152 * d[p*4+1] + 0.0722 * d[p*4+2]) * a + 255 * (1 - a);
    }

    // Stap 2: Histogramnormalisatie — strek het toonbereik (2e–98e percentiel)
    // Zelfde als Python: np.percentile(gray, (2, 98))
    const hist = new Uint32Array(256);
    for (let p = 0; p < n; p++) hist[Math.min(255, Math.round(gray[p]))]++;
    const lo2 = Math.floor(n * 0.02), hi98 = Math.floor(n * 0.98);
    let lo = 0, hi = 255, cum = 0;
    for (let i = 0; i < 256; i++) { cum += hist[i]; if (cum <= lo2) lo = i; }
    cum = 0;
    for (let i = 0; i < 256; i++) { cum += hist[i]; if (cum <= hi98) hi = i; }
    if (hi > lo) {
      const range = hi - lo;
      for (let p = 0; p < n; p++)
        gray[p] = Math.max(0, Math.min(255, (gray[p] - lo) / range * 255));
    }

    // Stap 3: Unsharp mask — scherpe randen geven fijnere dithering
    const blurred = _boxBlur(gray, w, h);
    for (let p = 0; p < n; p++) {
      gray[p] = Math.max(0, Math.min(255, gray[p] + 1.2 * (gray[p] - blurred[p])));
    }

    // Stap 4: Jarvis dithering
    const buf = Float32Array.from(gray);
    const out = new Uint8Array(w * h);
    const sp  = [[1,0,7],[2,0,5],[-2,1,3],[-1,1,5],[0,1,7],[1,1,5],[2,1,3],
                 [-2,2,1],[-1,2,3],[0,2,5],[1,2,3],[2,2,1]];
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = y * w + x;
        const old = Math.max(0, Math.min(255, buf[idx]));
        const nw  = old < 128 ? 0 : 255;
        out[idx]  = nw;
        const err = old - nw;
        if (err !== 0) for (const [dx, dy, wt] of sp) {
          const nx = x + dx, ny = y + dy;
          if (nx >= 0 && nx < w && ny < h) buf[ny * w + nx] += err * wt / 48;
        }
      }
    }

    for (let p = 0; p < out.length; p++) {
      const v = out[p];
      d[p*4] = d[p*4+1] = d[p*4+2] = v;
      d[p*4+3] = v === 255 ? 0 : 255; // wit = niet gegraveerd = transparant
    }
    ctx.putImageData(new ImageData(d, w, h), 0, 0);
  }

  /* --------------------------------------------------
     Image processing — sharp engraving effect
  -------------------------------------------------- */
  function _engrave(src, w, h) {
    const n = w * h;

    // Step 1: perceptual grayscale
    const gray = new Float32Array(n);
    for (let p = 0; p < n; p++) {
      const i = p * 4;
      gray[p] = 0.2126 * src[i] + 0.7152 * src[i + 1] + 0.0722 * src[i + 2];
    }

    // Step 2: unsharp mask — sharpen edges
    const blurred = _boxBlur(gray, w, h);
    const AMOUNT  = 1.1;
    for (let p = 0; p < n; p++) {
      gray[p] = Math.max(0, Math.min(255, gray[p] + AMOUNT * (gray[p] - blurred[p])));
    }

    // Step 3: tone curve — darken midtones, cap highlights (never pure white)
    const out = new Uint8ClampedArray(n * 4);
    for (let p = 0; p < n; p++) {
      const i = p * 4;
      const v = Math.round(Math.pow(gray[p] / 255, 1.28) * 190);
      out[i] = out[i + 1] = out[i + 2] = v;
      out[i + 3] = 255;
    }
    return out;
  }

  function _boxBlur(data, w, h) {
    const out = new Float32Array(data.length);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let sum = 0, count = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const px = Math.max(0, Math.min(w - 1, x + dx));
            const py = Math.max(0, Math.min(h - 1, y + dy));
            sum += data[py * w + px];
            count++;
          }
        }
        out[y * w + x] = sum / count;
      }
    }
    return out;
  }

  // Radial gradient halo: opaque white in the center, fading to transparent at the edge.
  // Roads within the circle are blocked; the soft fade hides the hard circle boundary.
  // In the engraving this becomes a natural vignette from unengraved wood to the map.
  const _MH = '<defs>'
            + '<radialGradient id="rg" cx="50" cy="50" r="38" gradientUnits="userSpaceOnUse">'
            + '<stop offset="75%" stop-color="white" stop-opacity="1"/>'
            + '<stop offset="100%" stop-color="white" stop-opacity="0"/>'
            + '</radialGradient>'
            + '</defs>';

  const MAP_MARKER_SVGS = {
    heart: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">${_MH}<circle cx="50" cy="50" r="38" fill="url(#rg)"/><g transform="translate(50 50) scale(0.45) translate(-50 -50)"><path fill="#000" d="M50 86.5 C 18 64 8 47 8 32 C 8 19 18 10 30 10 C 38 10 45 14 50 21 C 55 14 62 10 70 10 C 82 10 92 19 92 32 C 92 47 82 64 50 86.5 Z"/></g></svg>`,
    house: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">${_MH}<circle cx="50" cy="50" r="38" fill="url(#rg)"/><g transform="translate(50 50) scale(0.45) translate(-50 -50)"><path fill="#000" d="M50 10 L6 46 L14 46 L14 90 L40 90 L40 62 L60 62 L60 90 L86 90 L86 46 L94 46 Z"/></g></svg>`,
    pin:   `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">${_MH}<circle cx="50" cy="50" r="38" fill="url(#rg)"/><g transform="translate(50 50) scale(0.45) translate(-50 -50)"><path fill="#000" fill-rule="evenodd" d="M50 6 C 31 6 16 21 16 40 C 16 64 50 94 50 94 C 50 94 84 64 84 40 C 84 21 69 6 50 6 Z M50 52 C 43 52 38 46 38 40 C 38 33 43 28 50 28 C 57 28 62 33 62 40 C 62 46 57 52 50 52 Z"/></g></svg>`,
    wine:  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">${_MH}<circle cx="50" cy="50" r="38" fill="url(#rg)"/><g transform="translate(50 50) scale(0.45) translate(-50 -50)"><path fill="#000" d="M26 8 L74 8 C 74 30 70 50 54 54 L54 82 L72 82 L72 92 L28 92 L28 82 L46 82 L46 54 C 30 50 26 30 26 8 Z"/></g></svg>`,
    ring:  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">${_MH}<circle cx="50" cy="50" r="38" fill="url(#rg)"/><g transform="translate(50 50) scale(0.45) translate(-50 -50)"><path fill="#000" d="M50 36 C 38 28 32 22 32 16 C 32 11 36 8 40 8 C 44 8 48 11 50 14 C 52 11 56 8 60 8 C 64 8 68 11 68 16 C 68 22 62 28 50 36 Z"/><path fill="#000" fill-rule="evenodd" d="M66 42 C 50 42 38 52 38 66 C 38 80 50 90 66 90 C 82 90 92 80 92 66 C 92 52 82 42 66 42 Z M66 52 C 74 52 82 58 82 66 C 82 76 74 80 66 80 C 58 80 48 76 48 66 C 48 58 58 52 66 52 Z"/><path fill="#000" fill-rule="evenodd" d="M34 42 C 18 42 8 52 8 66 C 8 80 18 90 34 90 C 50 90 62 80 62 66 C 62 52 50 42 34 42 Z M34 52 C 42 52 52 58 52 66 C 52 76 42 80 34 80 C 26 80 18 76 18 66 C 18 58 26 52 34 52 Z"/></g></svg>`
  };

  function _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Strip editor-specific metadata and non-standard namespace declarations from SVG source.
  // Uses generic rules so it works for any editor (Inkscape, Affinity Designer, Illustrator, etc.).
  function _sanitizeSvgContent(str) {
    // Remove editor-specific elements that use namespace-prefixed tags
    str = str.replace(/<[a-zA-Z][a-zA-Z0-9_-]*:[a-zA-Z][a-zA-Z0-9_-]*[\s\S]*?<\/[a-zA-Z][a-zA-Z0-9_-]*:[a-zA-Z][a-zA-Z0-9_-]*\s*>/gi, '');
    str = str.replace(/<[a-zA-Z][a-zA-Z0-9_-]*:[a-zA-Z][a-zA-Z0-9_-]*[^>]*\/>/gi, '');
    str = str.replace(/<metadata[\s\S]*?<\/metadata\s*>/gi, '');
    // xlink:href → href (deprecated in SVG 1.1; strict XML parsers reject undeclared xlink prefix)
    str = str.replace(/\bxlink:href=/g, 'href=');
    // Strip ALL non-standard xmlns declarations (keeps xml: which is always predefined in XML)
    str = str.replace(/\s+xmlns:(?!xml\b)[a-zA-Z][a-zA-Z0-9_-]*="[^"]*"/gi, '');
    // Strip ALL namespace-prefixed attributes (except xml:* which is always valid)
    str = str.replace(/\s+(?!xml:)[a-zA-Z][a-zA-Z0-9_-]*:[a-zA-Z_][a-zA-Z0-9_-]*(?:="[^"]*"|='[^']*')/g, '');
    return _forceSvgBlack(str.trim());
  }

  // Normalize all colors in an SVG string to black (for laser engraving preview).
  // Pure regex — avoids DOM/HTML-parser roundtrip issues.
  // Preserves fill="none" and stroke="none" (transparent/outline-only shapes).
  function _forceSvgBlack(svgStr) {
    // 1. Colors inside <style>...</style> blocks (CSS classes / rules)
    svgStr = svgStr.replace(/(<style[^>]*>)([\s\S]*?)(<\/style>)/gi, (_, open, css, close) => {
      css = css
        .replace(/\bfill\s*:\s*(?!none\b)[^;}"]+/gi,       'fill:#000000')
        .replace(/\bstroke\s*:\s*(?!none\b)[^;}"]+/gi,     'stroke:#000000')
        .replace(/\bstop-color\s*:\s*(?!none\b)[^;}"]+/gi, 'stop-color:#000000')
        .replace(/\bcolor\s*:\s*(?!none\b)[^;}"]+/gi,      'color:#000000');
      return open + css + close;
    });
    // 2. Presentation attributes  fill="…"  stroke="…"  stop-color="…"
    svgStr = svgStr.replace(/\bfill="(?!none")([^"]*)"/gi,       'fill="#000000"');
    svgStr = svgStr.replace(/\bstroke="(?!none")([^"]*)"/gi,     'stroke="#000000"');
    svgStr = svgStr.replace(/\bstop-color="(?!none")([^"]*)"/gi, 'stop-color="#000000"');
    // 3. Inline style="…" attributes
    svgStr = svgStr.replace(/\bstyle="([^"]*)"/gi, (_, s) => 'style="' + s
      .replace(/\bfill\s*:\s*(?!none\b)[^;]+/gi,       'fill:#000000')
      .replace(/\bstroke\s*:\s*(?!none\b)[^;]+/gi,     'stroke:#000000')
      .replace(/\bstop-color\s*:\s*(?!none\b)[^;]+/gi, 'stop-color:#000000')
      .replace(/\bcolor\s*:\s*(?!none\b)[^;]+/gi,      'color:#000000')
    + '"');
    // 4. Ensure root <svg> defaults to black when nothing else sets fill
    svgStr = svgStr.replace(/^(\s*<svg\b(?![^>]*\bfill=))/i, '$1 fill="#000000"');
    return svgStr;
  }

  // Parse SVG content as XML and return false if there are any parse errors
  function _isSvgValid(svgStr) {
    try {
      const doc = new DOMParser().parseFromString(svgStr, 'image/svg+xml');
      return !doc.querySelector('parsererror');
    } catch (_) {
      return false;
    }
  }

  // Wrap text into lines fitting maxWidth (ctx must have font set); respects existing \n
  function _wrapTextToLines(ctx, text, maxWidth) {
    if (!text) return [''];
    const lines = [];
    for (const para of text.split('\n')) {
      const words = para.split(' ');
      let current = '';
      for (const word of words) {
        const test = current ? current + ' ' + word : word;
        if (current && ctx.measureText(test).width > maxWidth) {
          lines.push(current);
          current = word;
        } else {
          current = test;
        }
      }
      lines.push(current);
    }
    return lines.length ? lines : [''];
  }

  function _debounce(fn, ms) {
    let t;
    return function (...args) { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), ms); };
  }

  // Rotate a background image via an offscreen canvas and return a Promise<dataUrl>.
  // Degrees must be 0, 90, 180 or 270. Falls back to the original url on CORS errors.
  function _rotateBg(url, degrees) {
    degrees = ((degrees || 0) % 360 + 360) % 360;
    if (!degrees) return Promise.resolve(url);
    return new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        var swap   = degrees === 90 || degrees === 270;
        var canvas = document.createElement('canvas');
        canvas.width  = swap ? img.naturalHeight : img.naturalWidth;
        canvas.height = swap ? img.naturalWidth  : img.naturalHeight;
        var ctx = canvas.getContext('2d');
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(degrees * Math.PI / 180);
        ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = function () { resolve(url); };
      img.src = url;
    });
  }

  function _rotateSafezone(cfg, degrees) {
    if (!cfg) return cfg;
    var norm = ((degrees || 0) % 360 + 360) % 360;
    if (!norm) return cfg;
    var x = cfg.x, y = cfg.y, w = cfg.width, h = cfg.height;
    if (norm === 90)  return { x: 100 - y - h, y: x,           width: h, height: w };
    if (norm === 180) return { x: 100 - x - w, y: 100 - y - h, width: w, height: h };
    if (norm === 270) return { x: y,            y: 100 - x - w, width: h, height: w };
    return cfg;
  }

  // Returns { left, top } as percentages for map positioning within safezone.
  // position: 'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' |
  //           'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' | null
  function _getMapOffsets(position, sizePct) {
    var s     = Math.max(10, Math.min(100, sizePct || 90));
    var avail = 100 - s;
    var mid   = avail / 2;
    var vMap  = { top: 0, center: mid, bottom: avail };
    var hMap  = { left: 0, center: mid, right: avail };
    if (!position || position === 'center') return { left: mid, top: mid };
    var parts = position.split('-');
    return {
      top:  vMap[parts[0]] !== undefined ? vMap[parts[0]] : mid,
      left: parts[1]       ? (hMap[parts[1]] !== undefined ? hMap[parts[1]] : mid) : mid,
    };
  }

  function _imgContentRect(img) {
    const ew = img.offsetWidth;
    const eh = img.offsetHeight;
    const nw = img.naturalWidth  || ew;
    const nh = img.naturalHeight || eh;
    if (!ew || !eh || !nw || !nh) return { left: 0, top: 0, width: ew, height: eh };
    const scale = Math.min(ew / nw, eh / nh);
    const cw = Math.round(nw * scale);
    const ch = Math.round(nh * scale);
    return {
      left:   Math.round((ew - cw) / 2),
      top:    Math.round((eh - ch) / 2),
      width:  cw,
      height: ch
    };
  }

  /* --------------------------------------------------
     Bootstrap
  -------------------------------------------------- */
  const p = new Personalizer();
  window._personalizer = p; // referenced by PZElement._bindHandlers / _applyTransform

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => p.init());
  } else {
    p.init();
  }

})();